import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Switch,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import moment from "moment";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { programmeListing } from "src/DAL/Programme/Programme";
import {
  createFormQuestionFromLibrary,
  createTrackerSpecificFormQuestion,
  createEmptyForm,
  createEmptyStatus,
  CUSTOM_SCHEDULE_UNITS,
  DEFAULT_STATUS_COLORS,
  ensureTrackerShape,
  FORM_QUESTION_SOURCES,
  FORM_ROLE_OPTIONS,
  getCategoryLabel,
  getEffectiveFormQuestions,
  isFirstSubmissionForm,
  isPersonalInfoLibraryId,
  buildSeriesFormsFromSchedule,
  countSubmissionCheckpoints,
  createCoachReviewQuestion,
  createCoachReviewQuestionFromLibrary,
  createDefaultCoachReview,
  createDefaultPermissions,
  createAllCoachPermissionFlags,
  COACH_PERMISSION_MODE_OPTIONS,
  COACH_PERMISSION_MODES,
  COACH_PERMISSION_OPTIONS,
  COACH_REVIEW_FIELD_TYPES,
  COACH_REVIEW_TRIGGERS,
  createDefaultJourneyCreateSetup,
  createEmptyReviewPointSetting,
  formatReviewPointLabel,
  describeDurationWeeks,
  describeJourneyWindowFromPublish,
  formatDisplayDate,
  addDaysToDateString,
  getReviewPointDateFromPublish,
  getTodayDateString,
  getCoachReviewTriggerForMode,
  getDurationBasedSchedulePoints,
  getFormsForQuestionPattern,
  getJourneyStartLabel,
  getKindLabel,
  getMaxReviewDay,
  getMaxReviewWeek,
  getOpenScheduleLabel,
  getSubmissionScheduleLabel,
  getTrackerPublicUrl,
  isJourneyDurationUnlimited,
  isLeadCaptureTracker,
  JOURNEY_DURATION_OPTIONS,
  JOURNEY_START_OPTIONS,
  MEMBER_CHECKIN_MODE_OPTIONS,
  MONTH_DAY_OPTIONS,
  MONTHLY_OPTIONS,
  MONTH_OPTIONS,
  normalizeAlsoReviewEveryCheckin,
  normalizeCheckInMode,
  normalizeCheckInMonthlyStyle,
  normalizeCheckInMonthWeekOrdinal,
  normalizeCheckInMonthWeekday,
  normalizeCoachWithPermissions,
  normalizeJourneyCoachReviewSetup,
  normalizeJourneyStartsFrom,
  normalizeScheduledReviews,
  normalizeTrackerAccess,
  patchJourneyCreateSetup,
  QUESTION_PATTERN_OPTIONS,
  RELATIVE_OFFSET_UNITS,
  REPLY_WITHIN_DAYS_OPTIONS,
  resolveDurationFromProgramme,
  REVIEW_POINT_VIEW_THRESHOLD,
  getSchedulePointUnit,
  REVIEW_SCHEDULE_TYPE_OPTIONS,
  REVIEW_SCHEDULE_TYPES,
  SCHEDULED_REVIEWS_OPTIONS,
  syncReviewPointSettings,
  buildSubmissionScheduleFromReviewPoints,
  toTrackerApiPayload,
  TRACKER_ACCESS,
  TRACKER_ACCESS_OPTIONS,
  TRACKER_STATUS,
  UNLOCK_OPTIONS,
  WEEKDAY_OPTIONS,
  WEEK_AND_DAY_OPTIONS,
  WEEK_DAY_OPTIONS,
  withProgrammePublicRules,
  describeCheckInCadence,
  normalizeTracker,
} from "./trackerStorage";
import {
  getFieldTypeLabel,
  getMeasureTypeLabel,
  getQuestionLibrary,
  getQuestionModeLabel,
  getScaleOptions,
  loadQuestionLibraryFromApi,
  QUESTION_FIELD_TYPES,
  QUESTION_MODES,
} from "./questions/questionStorage";
import AddOrEditQuestionDrawer from "./questions/AddOrEditQuestionDrawer";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditFormFieldsDialog from "./EditFormFieldsDialog";
import {
  coachAvatarColor,
  coachDisplayName,
  coachInitials,
  normalizeCoachRef,
} from "./AddCoachesPopup";
import TrackerQrCodePopup from "./TrackerQrCodePopup";
import PublishSettingsDrawer from "./PublishSettingsDrawer";
import { buildDraftFromIntent } from "./CreateTrackerIntentPage";
import TrackerLibraryTagPicker from "./tags/TrackerLibraryTagPicker";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { active_coach_list } from "src/DAL/Menu/Menu";
import {
  addTrackerApi,
  getTrackerDetailApi,
  updateTrackerApi,
} from "../../DAL/tracker/tracker";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const PROGRAMME_REQUIRED_MESSAGE = "Select at least one programme";

/** GymRite members access needs at least one programme to be visible. */
const isMissingRequiredProgramme = (tracker) =>
  normalizeTrackerAccess(tracker?.trackerAccess, tracker) ===
    TRACKER_ACCESS.GYMRITE_MEMBERS && !(tracker?.programmes || []).length;

/** Trimmed, case-insensitive question label used to spot duplicates. */
const normalizeQuestionLabel = (label) =>
  String(label || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

/** 12-col form grid (Basics / How long? same pattern). */
const formGrid12 = {
  xs: "1fr",
  md: "repeat(12, minmax(0, 1fr))",
};
const formCol6 = { xs: "1 / -1", md: "span 6" };
const formCol4 = { xs: "1 / -1", md: "span 4" };
const formCol3 = { xs: "1 / -1", md: "span 3" };

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: PRIMARY },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: PRIMARY,
  },
};

/** ⓘ in the top-right of a field; hover/focus explains where it shows. */
const FieldInfoAdornment = ({ text }) => (
  <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1.25 }}>
    <Tooltip title={text} arrow placement="top">
      <InfoOutlinedIcon
        tabIndex={0}
        aria-label={text}
        sx={{ fontSize: 18, color: MUTED, cursor: "help" }}
      />
    </Tooltip>
  </InputAdornment>
);

const SectionCard = ({
  id,
  icon,
  title,
  description,
  subtitle,
  action,
  children,
  sx,
  // Space between the card body items (question lists use less).
  gap = 2.5,
}) => (
  <Box
    id={id}
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "12px",
      bgcolor: "#fff",
      mb: 3,
      overflow: "hidden",
      boxShadow: "none",
      ...sx,
    }}
  >
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        pt: 2.25,
        pb: title || description || action ? 0.5 : 0,
        display: "flex",
        // Title sits level with the buttons; a subtitle keeps it top-aligned.
        alignItems: subtitle ? "flex-start" : "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", gap: 0.75, minWidth: 0 }}>
        {icon && (
          <Box sx={{ color: PRIMARY, mt: 0.2, flexShrink: 0 }}>{icon}</Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
              {title}
            </Typography>
          ) : null}
          {/* Short "what is this for" line; `description` stays hidden. */}
          {subtitle ? (
            <Typography
              sx={{ mt: 0.5, fontSize: 12.5, color: MUTED, lineHeight: 1.5 }}
            >
              {subtitle}
            </Typography>
          ) : null}
          {/* {description ? (
            <Typography
              sx={{ mt: 0.5, fontSize: 12.5, color: MUTED, lineHeight: 1.5 }}
            >
              {description}
            </Typography>
          ) : null} */}
        </Box>
      </Box>
      {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
    </Box>
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        // Tighter under a header — the action button already adds height.
        // A subtitle needs room before the first outlined field label.
        pt: subtitle ? 2 : title || action ? 0.75 : 2.25,
        pb: 2.5,
        display: "flex",
        flexDirection: "column",
        gap,
      }}
    >
      {children}
    </Box>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{ mb: 0.85, fontWeight: 600, fontSize: 13, color: "#334155" }}>
    {children}
  </Typography>
);

const ChoicePill = ({ label, selected, disabled, onClick, grow = true }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      flex: grow ? 1 : "0 0 auto",
      textAlign: "center",
      px: grow ? 1.5 : 1.35,
      py: grow ? 1.1 : 0.65,
      borderRadius: "8px",
      border: "1.5px solid",
      borderColor: selected ? PRIMARY : BORDER,
      bgcolor: selected ? PRIMARY_SOFT : "#fff",
      color: selected ? "#0f172a" : MUTED,
      fontWeight: 600,
      fontSize: grow ? 13 : 12.5,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
      boxShadow: selected ? `0 0 0 1px ${PRIMARY}` : "none",
      userSelect: "none",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

/** Bordered panel used inside Coach review / Coaches sections. */
const NestedPanel = ({ title, children, sx }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: "10px",
      p: 2,
      bgcolor: "#fff",
      ...sx,
    }}
  >
    {title ? (
      <Typography
        sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a", mb: 1.5 }}
      >
        {title}
      </Typography>
    ) : null}
    {children}
  </Box>
);

const SegmentedToggle = ({
  options,
  value,
  onChange,
  disabledValues = [],
  compact = false,
}) => (
  <Box
    sx={{
      display: "inline-flex",
      flexWrap: "wrap",
      gap: 0.65,
      maxWidth: "100%",
    }}
  >
    {options.map((option) => {
      const selected = value === option.value;
      const disabled = disabledValues.includes(option.value);
      return (
        <Box
          key={String(option.value)}
          onClick={disabled ? undefined : () => onChange(option.value)}
          sx={{
            flex: "0 0 auto",
            textAlign: "center",
            px: compact ? 1.25 : 1.5,
            py: compact ? 0.7 : 0.85,
            borderRadius: "8px",
            border: "1.5px solid",
            borderColor: selected ? PRIMARY : BORDER,
            bgcolor: selected ? PRIMARY : "#fff",
            color: selected ? "#fff" : "#334155",
            fontWeight: 600,
            fontSize: compact ? 12.5 : 13,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.45 : 1,
            userSelect: "none",
            whiteSpace: "nowrap",
            lineHeight: 1.25,
          }}
        >
          {option.label}
        </Box>
      );
    })}
  </Box>
);

const SelectChip = ({ label, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      minWidth: 36,
      height: 32,
      px: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      border: "1.5px solid",
      borderColor: selected ? PRIMARY : BORDER,
      bgcolor: selected ? PRIMARY : "#fff",
      color: selected ? "#fff" : "#334155",
      fontWeight: 600,
      fontSize: 12.5,
      cursor: "pointer",
      userSelect: "none",
    }}
  >
    {label}
  </Box>
);

const PatternCard = ({ option, selected, onClick, locked = false }) => (
  <Box
    onClick={locked ? undefined : onClick}
    sx={{
      border: "1.5px solid",
      borderColor: selected ? PRIMARY : BORDER,
      bgcolor: selected ? PRIMARY_SOFT : "#fff",
      borderRadius: "10px",
      p: 2,
      height: "100%",
      cursor: locked ? "default" : "pointer",
      opacity: locked && !selected ? 0.5 : 1,
      boxShadow: selected ? `0 0 0 1px ${PRIMARY}` : "none",
      transition: "all 0.15s ease",
      "&:hover": locked
        ? undefined
        : {
            borderColor: selected ? PRIMARY : "#cbd5e1",
            bgcolor: selected ? PRIMARY_SOFT : "#f8fafc",
          },
    }}
  >
    <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#0f172a", mb: 0.75 }}>
      {option.title}
    </Typography>
    <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.45 }}>
      {option.description}
    </Typography>
  </Box>
);

const StatusPill = ({ name, color }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1.1,
      py: 0.35,
      borderRadius: "999px",
      border: `1.5px solid ${color}`,
      color,
      bgcolor: `${color}14`,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {name}
  </Box>
);

const formatEditedAgo = (iso) => {
  if (!iso) return "just now";
  const m = moment(iso);
  if (!m.isValid()) return "just now";
  return m.fromNow();
};

export default function AddOrEditTracker() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, intent } = useParams();
  const isCreate = Boolean(intent);
  const { enqueueSnackbar } = useSnackbar();

  const [tracker, setTracker] = useState(null);
  const [pageLoading, setPageLoading] = useState(!isCreate);
  const [builderTab, setBuilderTab] = useState(
    location.state?.builderTab === "questions" ||
      location.state?.builderTab === "coaches" ||
      location.state?.builderTab === "settings"
      ? location.state.builderTab
      : "basics"
  );
  const [questionsVisited, setQuestionsVisited] = useState(false);
  const [savedTabs, setSavedTabs] = useState(
    location.state?.savedTabs || {
      basics: false,
      questions: false,
      coaches: false,
      settings: false,
    }
  );
  const [isSaving, setIsSaving] = useState(false);
  // Publish also sets isSaving (both buttons disabled); this picks the label.
  const [isPublishing, setIsPublishing] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  // Programme error only after the field is left empty or a save is tried.
  const [programmeTouched, setProgrammeTouched] = useState(false);
  // Duration Period left empty and a save was tried (journeys).
  const [durationTouched, setDurationTouched] = useState(false);
  const [editingFormId, setEditingFormId] = useState(null);
  const [programmeOptions, setProgrammeOptions] = useState([]);
  const [programmesLoading, setProgrammesLoading] = useState(false);
  const [coachRoster, setCoachRoster] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(false);
  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const [sharedLibraryOpen, setSharedLibraryOpen] = useState(false);
  // Library question being edited from the shared-questions picker.
  const [editingLibraryQuestion, setEditingLibraryQuestion] = useState(null);
  const [selectedSharedLibraryIds, setSelectedSharedLibraryIds] = useState([]);
  const [sharedLibraryTab, setSharedLibraryTab] = useState("all");
  const [sharedLibraryFilter, setSharedLibraryFilter] = useState({ measurable: false });
  const [sharedLibrarySearch, setSharedLibrarySearch] = useState("");
  const [coachLibraryOpen, setCoachLibraryOpen] = useState(false);
  const [selectedCoachLibraryIds, setSelectedCoachLibraryIds] = useState([]);
  const [coachLibrarySearch, setCoachLibrarySearch] = useState("");
  const [schedulePointsExpanded, setSchedulePointsExpanded] = useState(false);
  const [editingReviewPointKey, setEditingReviewPointKey] = useState(null);
  // Empty by default (0) — the admin types the number.
  const [customPointAmount, setCustomPointAmount] = useState("0");
  const [addPointOpen, setAddPointOpen] = useState(false);
  const [fixedDateMonth, setFixedDateMonth] = useState(1);
  const [fixedDateDay, setFixedDateDay] = useState("1");
  const [reviewDayToAdd, setReviewDayToAdd] = useState("1");
  const [reviewWeekToAdd, setReviewWeekToAdd] = useState("1");
  const [permissionsExpandedCoachId, setPermissionsExpandedCoachId] =
    useState(null);
  const [libraryQuestions, setLibraryQuestions] = useState(() =>
    getQuestionLibrary()
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await loadQuestionLibraryFromApi();
      if (cancelled) return;
      setLibraryQuestions(result.questions);
      if (!result.ok) {
        enqueueSnackbar(result.message, { variant: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (isCreate) {
      if (intent !== "journey" && intent !== "blank" && intent !== "lead") {
        navigate("/trackers/new", { replace: true });
        return undefined;
      }
      let cancelled = false;
      const buildDraft = async () => {
        // Lead drafts pre-fill shared questions from the API question library.
        if (intent === "lead") {
          setPageLoading(true);
          await loadQuestionLibraryFromApi();
          if (cancelled) return;
        }
        setPageLoading(false);
        setTracker(ensureTrackerShape(buildDraftFromIntent(intent)));
        setBuilderTab("basics");
        setQuestionsVisited(false);
        setSavedTabs({
          basics: false,
          questions: false,
          coaches: false,
          settings: false,
        });
      };
      buildDraft();
      return () => {
        cancelled = true;
      };
    }

    const loadExistingTracker = async () => {
      setPageLoading(true);
      let existing = null;
      let message = "Tracker not found";
      try {
        const res = await getTrackerDetailApi(id);
        if (res?.code === 200 && res?.tracker) {
          existing = normalizeTracker(res.tracker);
        } else if (res?.message) {
          message = res.message;
        }
      } catch {
        message = "Could not load tracker";
      }
      if (!existing) {
        setPageLoading(false);
        enqueueSnackbar(message, { variant: "error" });
        navigate("/trackers");
        return;
      }
      setTracker(existing);
      setPageLoading(false);
      const navState = location.state || {};
      // Same count as the Questions tab gate: check-in questions also live
      // in shared / personal info, not only forms[].questions.
      const formQ =
        (existing.forms || []).reduce(
          (sum, form) => sum + (form.questions || []).length,
          0
        ) +
        (existing.sharedQuestions || []).length +
        (existing.personalInfoQuestions || []).length;
      const coachesCount = (existing.coaches || []).length;
      const fromCreateSave = Boolean(navState.savedTabs?.basics);
      if (fromCreateSave) {
        setSavedTabs(navState.savedTabs);
        setQuestionsVisited(Boolean(navState.builderTab === "questions"));
      } else {
        setSavedTabs({
          basics: true,
          questions: formQ > 0,
          coaches: coachesCount > 0,
        });
        setQuestionsVisited(formQ > 0);
      }
      let nextTab = null;
      if (navState.builderTab === "questions") {
        nextTab = "questions";
      } else if (navState.builderTab === "coaches") {
        nextTab = isLeadCaptureTracker(existing) ? "questions" : "coaches";
      }
      if (nextTab) {
        setBuilderTab(nextTab);
      }
    };

    loadExistingTracker();
  }, [id, intent, isCreate, enqueueSnackbar, navigate]);

  useEffect(() => {
    let active = true;
    const loadProgrammes = async () => {
      setProgrammesLoading(true);
      try {
        const result = await programmeListing("", "active", "");
        if (!active) return;
        if (result.code === 200) {
          const list = (result.program || result.programs || []).map(
            (program) => ({
              id: program._id || program.id,
              title: program.title || "",
              program_length_type: program.program_length_type || "",
              no_of_weeks: program.no_of_weeks,
            })
          );
          setProgrammeOptions(list.filter((item) => item.id && item.title));
        } else {
          enqueueSnackbar(result.message || "Could not load programmes", {
            variant: "error",
          });
        }
      } catch (error) {
        if (active) {
          enqueueSnackbar("Could not load programmes", { variant: "error" });
        }
      } finally {
        if (active) setProgrammesLoading(false);
      }
    };
    loadProgrammes();
    return () => {
      active = false;
    };
  }, [enqueueSnackbar]);

  const update = (patch) => {
    setTracker((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateCreateSetup = (patch) => {
    if (
      patch &&
      (Object.prototype.hasOwnProperty.call(patch, "durationWeeks") ||
        Object.prototype.hasOwnProperty.call(patch, "customDurationDays"))
    ) {
      setSchedulePointsExpanded(false);
      setEditingReviewPointKey(null);
    }
    setTracker((prev) => {
      if (!prev) return prev;
      if (!prev.createSetup && prev.questionPattern === "one_form_once") {
        return prev;
      }
      const baseSetup = prev.createSetup || createDefaultJourneyCreateSetup();
      return patchJourneyCreateSetup(
        { ...prev, createSetup: baseSetup },
        patch
      );
    });
  };

  const updateSettings = (patch) => {
    setTracker((prev) =>
      prev
        ? {
            ...prev,
            settings: { ...(prev.settings || {}), ...patch },
          }
        : prev
    );
  };

  const handleProgrammesChange = (value) => {
    const nextProgrammes = (value || [])
      .filter((item) => item?.id)
      .map((item) => ({
        id: item.id,
        title: item.title,
        program_length_type: item.program_length_type,
        no_of_weeks: item.no_of_weeks,
      }));
    setTracker((prev) => {
      if (!prev) return prev;
      let next = withProgrammePublicRules({
        ...prev,
        trackerAccess: TRACKER_ACCESS.GYMRITE_MEMBERS,
        programmes: nextProgrammes,
      });

      if (prev.createSetup) {
        const primary = nextProgrammes[0] || null;
        const duration = resolveDurationFromProgramme(primary);
        const setupPatch =
          duration == null
            ? nextProgrammes.length
              ? { durationWeeks: "ongoing" }
              : {}
            : { durationWeeks: duration };
        if (Object.keys(setupPatch).length) {
          return patchJourneyCreateSetup(
            { ...next, createSetup: prev.createSetup },
            setupPatch
          );
        }
      }
      return next;
    });
  };

  const handleTrackerAccessChange = (access) => {
    const nextAccess = normalizeTrackerAccess(access);
    setTracker((prev) => {
      if (!prev) return prev;
      if (nextAccess === TRACKER_ACCESS.PUBLIC) {
        return {
          ...prev,
          trackerAccess: TRACKER_ACCESS.PUBLIC,
          programmes: [],
          settings: {
            ...(prev.settings || {}),
            acceptingSubmissions: true,
          },
          publicSlug:
            prev.publicSlug ||
            String(prev.name || "tracker")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "") ||
            prev.id,
        };
      }
      return withProgrammePublicRules({
        ...prev,
        trackerAccess: TRACKER_ACCESS.GYMRITE_MEMBERS,
      });
    });
  };

  const handleProgrammeSelect = (value) => {
    const next = Array.isArray(value) ? value : value ? [value] : [];
    handleProgrammesChange(next);
  };

  const updateNested = (key, value) => {
    setTracker((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  // Keep coachReview.trigger locked to Reviews controls (scheduled + also check-in).
  useEffect(() => {
    if (!tracker?.createSetup) return;
    const expected = getCoachReviewTriggerForMode(tracker.createSetup);
    const current = tracker.coachReview?.trigger;
    if (current === expected) return;
    setTracker((prev) => {
      if (!prev?.createSetup) return prev;
      const nextExpected = getCoachReviewTriggerForMode(prev.createSetup);
      if (prev.coachReview?.trigger === nextExpected) return prev;
      return {
        ...prev,
        coachReview: {
          ...(prev.coachReview || createDefaultCoachReview()),
          trigger: nextExpected,
        },
      };
    });
  }, [
    tracker?.id,
    tracker?.createSetup?.scheduledReviews,
    tracker?.createSetup?.alsoReviewEveryCheckin,
    tracker?.createSetup?.coachReviewMode,
    tracker?.coachReview?.trigger,
  ]);

  useEffect(() => {
    const list = tracker?.coaches || [];
    if (
      permissionsExpandedCoachId != null &&
      !list.some((c) => c.id === permissionsExpandedCoachId)
    ) {
      setPermissionsExpandedCoachId(null);
    }
  }, [tracker?.coaches, permissionsExpandedCoachId]);

  useEffect(() => {
    const needsCoaches =
      builderTab === "coaches" ||
      (builderTab === "questions" && isLeadCaptureTracker(tracker));
    if (!needsCoaches) {
      if (builderTab !== "coaches") {
        setPermissionsExpandedCoachId(null);
      }
      return;
    }
    let active = true;
    const load = async () => {
      setCoachesLoading(true);
      try {
        const result = await active_coach_list();
        if (!active) return;
        if (result.code === 200) {
          const list = (result.coach || [])
            .map((item) => normalizeCoachRef(item))
            .filter(Boolean);
          setCoachRoster(list);
        } else {
          setCoachRoster([]);
          enqueueSnackbar(result.message || "Could not load coaches", {
            variant: "error",
          });
        }
      } catch (error) {
        if (active) {
          setCoachRoster([]);
          enqueueSnackbar("Could not load coaches", { variant: "error" });
        }
      } finally {
        if (active) setCoachesLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [builderTab, tracker, enqueueSnackbar]);

  const handleAssignedCoachesChange = (nextRaw = []) => {
    const prevById = new Map((tracker.coaches || []).map((c) => [c.id, c]));
    const defaults = tracker.permissions || createDefaultPermissions();
    const next = (nextRaw || []).map((coach) => {
      const prev = prevById.get(coach.id);
      return normalizeCoachWithPermissions(
        {
          ...coach,
          permissionMode:
            prev?.permissionMode || coach.permissionMode || "all",
          permissions: prev?.permissions || coach.permissions,
        },
        defaults
      );
    });
    updateNested("coaches", next);
    if (
      permissionsExpandedCoachId != null &&
      !next.some((c) => c.id === permissionsExpandedCoachId)
    ) {
      setPermissionsExpandedCoachId(null);
    }
  };

  useEffect(() => {
    if (!tracker) return;
    if (!savedTabs.basics && builderTab !== "basics") {
      setBuilderTab("basics");
      return;
    }
    if (
      isLeadCaptureTracker(tracker) &&
      (builderTab === "coaches" || builderTab === "settings")
    ) {
      setBuilderTab(savedTabs.questions ? "questions" : "basics");
      return;
    }
    if (
      savedTabs.basics &&
      !savedTabs.questions &&
      (builderTab === "coaches" || builderTab === "settings")
    ) {
      setBuilderTab("questions");
      setQuestionsVisited(true);
    }
  }, [tracker, builderTab, savedTabs]);

  useEffect(() => {
    setEditingReviewPointKey(null);
  }, [builderTab]);

  const handleSave = async (options = {}) => {
    if (!tracker || isSaving) return;
    if (!tracker.name?.trim()) {
      enqueueSnackbar("Enter a name in Basics before saving", {
        variant: "warning",
      });
      setBuilderTab("basics");
      return;
    }
    if (tracker.createSetup?.durationWeeks === "") {
      setDurationTouched(true);
      enqueueSnackbar("Choose the duration period in Basics before saving", {
        variant: "warning",
      });
      setBuilderTab("basics");
      return;
    }
    if (isMissingRequiredProgramme(tracker)) {
      setProgrammeTouched(true);
      enqueueSnackbar(PROGRAMME_REQUIRED_MESSAGE, { variant: "warning" });
      setBuilderTab("basics");
      return;
    }

    setIsSaving(true);
    let shaped = null;
    const trackerDbId = tracker._id || tracker.id || (isCreate ? null : id);
    const looksLikeMongoId = /^[a-f\d]{24}$/i.test(String(trackerDbId || ""));
    const payload = toTrackerApiPayload({
      ...tracker,
      name: tracker.name.trim(),
      description: (tracker.description || "").trim(),
      visitorMessage: (tracker.visitorMessage || "").trim(),
      publicSlug:
        tracker.publicSlug ||
        String(tracker.name || "tracker")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, ""),
    });

    try {
      if (looksLikeMongoId) {
        const res = await updateTrackerApi(trackerDbId, payload);
        if (res?.code === 200 && res?.tracker) {
          shaped = normalizeTracker(res.tracker);
        } else {
          enqueueSnackbar(res?.message || "Could not save tracker", {
            variant: "error",
          });
          setIsSaving(false);
          return null;
        }
      } else {
        const res = await addTrackerApi(payload);
        if (res?.code === 200 && res?.tracker) {
          shaped = normalizeTracker(res.tracker);
        } else {
          enqueueSnackbar(res?.message || "Could not create tracker", {
            variant: "error",
          });
          setIsSaving(false);
          return null;
        }
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save tracker", {
        variant: "error",
      });
      setIsSaving(false);
      return null;
    }

    setTracker(shaped);
    setIsSaving(false);
    // Saved: the opened week folds away (it stayed open after Save & continue).
    setEditingReviewPointKey(null);

    if (builderTab === "basics") {
      const nextSaved = { ...savedTabs, basics: true };
      setSavedTabs(nextSaved);
      setQuestionsVisited(true);
      if (isCreate) {
        enqueueSnackbar(
          isLeadCaptureTracker(shaped)
            ? "Basics saved — continue with Configuration"
            : "Basics saved — continue with check-in & questions",
          {
            variant: "success",
          }
        );
        navigate(`/trackers/edit/${shaped.id}`, {
          replace: true,
          state: { builderTab: "questions", savedTabs: nextSaved },
        });
        return shaped;
      }
      setBuilderTab("questions");
      enqueueSnackbar("Basics saved", { variant: "success" });
      return shaped;
    }

    if (builderTab === "questions") {
      setSavedTabs((prev) => ({ ...prev, questions: true }));
      if (isLeadCaptureTracker(shaped)) {
        enqueueSnackbar("Lead capture configuration saved", { variant: "success" });
        navigate(`/trackers/${shaped.id}`);
        return shaped;
      }
      if (options?.stay === true) {
        enqueueSnackbar("Check-in configuration saved", { variant: "success" });
        return shaped;
      }
      setBuilderTab("coaches");
      enqueueSnackbar("Check-in configuration saved", { variant: "success" });
      return shaped;
    }

    if (builderTab === "coaches") {
      setSavedTabs((prev) => ({ ...prev, coaches: true }));
      if (options?.stay === true) {
        enqueueSnackbar("Coaches saved", { variant: "success" });
        return shaped;
      }
      setBuilderTab("settings");
      enqueueSnackbar("Coaches saved — continue with Check-in settings", {
        variant: "success",
      });
      return shaped;
    }

    if (builderTab === "settings") {
      setSavedTabs((prev) => ({ ...prev, settings: true }));
      enqueueSnackbar("Check-in & media settings saved", { variant: "success" });
      return shaped;
    }

    enqueueSnackbar("Tracker configuration saved", { variant: "success" });
    return shaped;
  };

  const handleQuestionsSaveAndContinue = () => {
    setQuestionsVisited(true);
    handleSave();
  };

  const handlePublishSettingsChange = (patch) => {
    setTracker((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const handlePublish = async () => {
    if (!tracker?.name?.trim()) {
      enqueueSnackbar("Enter a name in Basics before publishing", {
        variant: "warning",
      });
      setBuilderTab("basics");
      return;
    }
    if (isMissingRequiredProgramme(tracker)) {
      setProgrammeTouched(true);
      enqueueSnackbar(PROGRAMME_REQUIRED_MESSAGE, { variant: "warning" });
      setBuilderTab("basics");
      return;
    }
    // Members would get an empty form: a tracker goes live only with at least one question.
    const publishForms = tracker.forms || [];
    const hasQuestions = publishForms.some(
      (form) =>
        getEffectiveFormQuestions(form, tracker.sharedQuestions || [], [], publishForms)
          .length > 0
    );
    if (!hasQuestions) {
      enqueueSnackbar("Add at least one question before publishing", {
        variant: "warning",
      });
      setBuilderTab("questions");
      return;
    }
    const trackerDbId = tracker._id || tracker.id;
    if (isCreate || !/^[a-f\d]{24}$/i.test(String(trackerDbId || ""))) {
      enqueueSnackbar("Save the tracker first, then publish", {
        variant: "info",
      });
      return;
    }

    setIsSaving(true);
    setIsPublishing(true);
    const payload = toTrackerApiPayload({
      ...tracker,
      name: tracker.name.trim(),
      status: TRACKER_STATUS.LIVE,
      settings: {
        ...(tracker.settings || {}),
        acceptingSubmissions: true,
      },
    });

    try {
      const res = await updateTrackerApi(trackerDbId, payload);
      if (res?.code === 200 && res?.tracker) {
        setTracker(normalizeTracker(res.tracker));
        enqueueSnackbar("Tracker published", { variant: "success" });
      } else {
        enqueueSnackbar(res?.message || "Could not publish tracker", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not publish tracker", {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
      setIsPublishing(false);
    }
  };

  const handlePreviewForm = () => {
    const first =
      (tracker.forms || []).find((f) => f.role === "first_submission") ||
      (tracker.forms || [])[0];
    if (!first) {
      enqueueSnackbar("Add a form first", { variant: "warning" });
      return;
    }
    setEditingFormId(first.id);
  };

  const updateCoachAt = (coachId, patch) => {
    const list = tracker.coaches || [];
    const next = list.map((coach) => {
      if (coach.id !== coachId) return coach;
      return normalizeCoachWithPermissions(
        { ...coach, ...patch },
        tracker.permissions || createDefaultPermissions()
      );
    });
    updateNested("coaches", next);
  };

  const setCoachPermissionMode = (coachId, mode) => {
    const coach = (tracker.coaches || []).find((c) => c.id === coachId);
    if (!coach) return;
    if (mode === COACH_PERMISSION_MODES.ALL) {
      updateCoachAt(coachId, {
        permissionMode: COACH_PERMISSION_MODES.ALL,
        permissions: createAllCoachPermissionFlags(),
      });
      return;
    }
    updateCoachAt(coachId, {
      permissionMode: COACH_PERMISSION_MODES.SPECIFIC,
      permissions: {
        ...createAllCoachPermissionFlags(),
        ...(coach.permissions || {}),
      },
    });
  };

  const updateCoachPermissionFlag = (coachId, key, value) => {
    const coach = (tracker.coaches || []).find((c) => c.id === coachId);
    if (!coach) return;
    const flags = {
      ...(coach.permissionMode === COACH_PERMISSION_MODES.SPECIFIC
        ? coach.permissions || createAllCoachPermissionFlags()
        : createAllCoachPermissionFlags()),
      [key]: value,
    };
    if (key === "coachesCanCreateEntries" || key === "coachesCanUpdateEntries") {
      flags.coachesCanEditEntries =
        Boolean(flags.coachesCanCreateEntries) &&
        Boolean(flags.coachesCanUpdateEntries);
      flags.coachesCanSubmitOnBehalf = Boolean(flags.coachesCanCreateEntries);
    }
    if (key === "coachesCanChooseTriggerAutomation") {
      flags.coachesCanTriggerAutomation = value;
    }
    updateCoachAt(coachId, {
      permissionMode: COACH_PERMISSION_MODES.SPECIFIC,
      permissions: flags,
    });
  };

  const updateCoachReview = (patch) => {
    updateNested("coachReview", {
      ...(tracker.coachReview || createDefaultCoachReview()),
      ...patch,
    });
  };

  const addCoachReviewQuestion = () => {
    const review = tracker.coachReview || createDefaultCoachReview();
    const questions = review.questions || [];
    updateCoachReview({
      questions: [
        ...questions,
        createCoachReviewQuestion(
          {
            source: "tracker",
            label: "",
            fieldType: "long_text",
            required: true,
          },
          questions.length
        ),
      ],
    });
  };

  const addCoachReviewQuestionsFromLibrary = (libraryItems = []) => {
    if (!libraryItems.length) return;
    const review = tracker.coachReview || createDefaultCoachReview();
    const existing = review.questions || [];
    const linked = new Set(
      existing.map((q) => q.questionId).filter(Boolean)
    );
    const additions = libraryItems
      .filter((item) => item?.id && !linked.has(item.id))
      .map((item, index) =>
        createCoachReviewQuestionFromLibrary(item, existing.length + index)
      );
    if (!additions.length) return;
    updateCoachReview({
      questions: [...existing, ...additions],
    });
  };

  const updateCoachReviewQuestion = (id, patch) => {
    const review = tracker.coachReview || createDefaultCoachReview();
    updateCoachReview({
      questions: (review.questions || []).map((item) => {
        if (item.id !== id) return item;
        const fromLibrary = Boolean(item.questionId);
        if (fromLibrary) {
          const allowed = {};
          if (Object.prototype.hasOwnProperty.call(patch, "required")) {
            allowed.required = patch.required;
          }
          return { ...item, ...allowed };
        }
        return { ...item, ...patch };
      }),
    });
  };

  const removeCoachReviewQuestion = (id) => {
    const review = tracker.coachReview || createDefaultCoachReview();
    updateCoachReview({
      questions: (review.questions || [])
        .filter((item) => item.id !== id)
        .map((item, order) => ({ ...item, order })),
    });
  };

  const moveCoachReviewQuestion = (index, direction) => {
    const review = tracker.coachReview || createDefaultCoachReview();
    const list = [...(review.questions || [])];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    const temp = list[index];
    list[index] = list[target];
    list[target] = temp;
    updateCoachReview({
      questions: list.map((item, order) => ({ ...item, order })),
    });
  };

  const publicUrl = useMemo(() => getTrackerPublicUrl(tracker), [tracker]);

  const editingForm = useMemo(
    () => tracker?.forms?.find((form) => form.id === editingFormId) || null,
    [tracker, editingFormId]
  );

  const libraryForShared = useMemo(
    () => libraryQuestions,
    [libraryQuestions, sharedLibraryOpen, coachLibraryOpen]
  );
  const sharedLinkedLibraryIds = useMemo(
    () =>
      new Set(
        [
          ...(tracker?.sharedQuestions || []),
          ...(tracker?.personalInfoQuestions || []),
        ]
          .map((q) => q.questionId)
          .filter(Boolean)
      ),
    [tracker?.sharedQuestions, tracker?.personalInfoQuestions]
  );
  // Labels already on the tracker — hides same-named library duplicates.
  const sharedLinkedLabels = useMemo(() => {
    const libraryById = new Map(libraryQuestions.map((item) => [item.id, item]));
    return new Set(
      [
        ...(tracker?.sharedQuestions || []),
        ...(tracker?.personalInfoQuestions || []),
      ]
        .map((q) =>
          normalizeQuestionLabel(
            q.label || libraryById.get(q.questionId)?.label
          )
        )
        .filter(Boolean)
    );
  }, [
    libraryQuestions,
    tracker?.sharedQuestions,
    tracker?.personalInfoQuestions,
  ]);
  const availableSharedLibrary = useMemo(
    () =>
      libraryForShared.filter(
        (item) =>
          item.mode !== QUESTION_MODES.COACH_REVIEW &&
          !sharedLinkedLibraryIds.has(item.id) &&
          !(item.libraryKey && sharedLinkedLibraryIds.has(item.libraryKey)) &&
          !sharedLinkedLabels.has(normalizeQuestionLabel(item.label)) &&
          !isPersonalInfoLibraryId(item.libraryKey || item.id)
      ),
    [libraryForShared, sharedLinkedLibraryIds, sharedLinkedLabels]
  );
  const filteredSharedLibrary = useMemo(() => {
    return availableSharedLibrary.filter((item) => {
      if (
        sharedLibraryTab === "general" &&
        item.mode === QUESTION_MODES.MEASURABLE
      ) {
        return false;
      }
      if (
        sharedLibraryTab === "measurable" &&
        item.mode !== QUESTION_MODES.MEASURABLE
      ) {
        return false;
      }
      const q = sharedLibrarySearch.trim().toLowerCase();
      if (!q) return true;
      return `${item.label || ""}`.toLowerCase().includes(q);
    });
  }, [availableSharedLibrary, sharedLibraryTab, sharedLibrarySearch]);

  const coachLinkedLibraryIds = useMemo(
    () =>
      new Set(
        (tracker?.coachReview?.questions || [])
          .map((q) => q.questionId)
          .filter(Boolean)
      ),
    [tracker?.coachReview?.questions]
  );
  const availableCoachLibrary = useMemo(
    () =>
      libraryForShared.filter(
        (item) =>
          item.mode === QUESTION_MODES.COACH_REVIEW &&
          !coachLinkedLibraryIds.has(item.id)
      ),
    [libraryForShared, coachLinkedLibraryIds]
  );
  const filteredCoachLibrary = useMemo(() => {
    const q = coachLibrarySearch.trim().toLowerCase();
    if (!q) return availableCoachLibrary;
    return availableCoachLibrary.filter((item) =>
      `${item.label || ""}`.toLowerCase().includes(q)
    );
  }, [availableCoachLibrary, coachLibrarySearch]);

  const moveItem = (list, index, direction) => {
    const next = [...list];
    const target = index + direction;
    if (target < 0 || target >= next.length) return list;
    const temp = next[index];
    next[index] = next[target];
    next[target] = temp;
    return next;
  };

  const addForm = () => {
    const forms = tracker.forms || [];
    const form = createEmptyForm({
      name: `Form ${forms.length + 1}`,
      role: forms.length === 0 ? "first_submission" : "follow_up",
      unlockType: "after_days",
      unlockDays: forms.length === 0 ? 0 : 30,
    });
    updateNested("forms", [...forms, form]);
  };

  const updateForm = (formId, patch) => {
    updateNested(
      "forms",
      (tracker.forms || []).map((form) =>
        form.id === formId ? { ...form, ...patch } : form
      )
    );
  };

  const removeForm = (formId) => {
    updateNested(
      "forms",
      (tracker.forms || []).filter((form) => form.id !== formId)
    );
  };

  const openEditFields = (formId) => {
    setEditingFormId(formId);
  };

  const handleFormFieldsChange = (patch) => {
    if (!editingFormId) return;
    updateForm(editingFormId, patch);
  };

  const handleQuestionPatternChange = (pattern) => {
    const nextForms = getFormsForQuestionPattern(pattern, tracker.forms || [], {
      expandSeries: true,
    });
    update({ questionPattern: pattern, forms: nextForms });
  };

  const alignFormsToSchedule = () => {
    const nextForms = buildSeriesFormsFromSchedule(
      tracker.submissionSchedule,
      tracker.forms || []
    );
    updateNested("forms", nextForms);
    enqueueSnackbar("Forms updated to match submission schedule", {
      variant: "success",
    });
  };

  const removeSharedQuestion = (questionId) => {
    updateNested(
      "sharedQuestions",
      (tracker.sharedQuestions || []).filter((q) => q.id !== questionId)
    );
  };

  const updateSharedQuestion = (questionId, patch) => {
    updateNested(
      "sharedQuestions",
      (tracker.sharedQuestions || []).map((question) => {
        if (question.id !== questionId) return question;
        const fromLibrary =
          question.source === FORM_QUESTION_SOURCES.LIBRARY ||
          Boolean(question.questionId);
        if (fromLibrary) {
          // Library questions stay locked — only tracker config like required.
          const allowed = {};
          if (Object.prototype.hasOwnProperty.call(patch, "required")) {
            allowed.required = patch.required;
          }
          return { ...question, ...allowed };
        }
        return { ...question, ...patch };
      })
    );
  };

  const addSharedSpecificQuestion = () => {
    const existing = tracker.sharedQuestions || [];
    updateNested("sharedQuestions", [
      ...existing,
      createTrackerSpecificFormQuestion(
        {
          label: "",
          placeholder: "",
          fieldType: "short_text",
          required: false,
        },
        existing.length
      ),
    ]);
  };

  const confirmSharedLibraryPicks = () => {
    const library = libraryQuestions;
    const existingIds = new Set(
      (tracker.sharedQuestions || []).map((q) => q.questionId).filter(Boolean)
    );
    const existingLabels = new Set(sharedLinkedLabels);
    const toAdd = library
      .filter((item) => {
        if (
          !selectedSharedLibraryIds.includes(item.id) ||
          existingIds.has(item.id)
        ) {
          return false;
        }
        const label = normalizeQuestionLabel(item.label);
        if (label && existingLabels.has(label)) return false;
        if (label) existingLabels.add(label);
        return true;
      })
      .map((item, index) =>
        createFormQuestionFromLibrary(
          item,
          (tracker.sharedQuestions || []).length + index
        )
      );
    updateNested("sharedQuestions", [
      ...(tracker.sharedQuestions || []),
      ...toAdd,
    ]);
    setSharedLibraryOpen(false);
    setSelectedSharedLibraryIds([]);
  };

  const addStatus = () => {
    const statuses = tracker.statuses || [];
    const color =
      DEFAULT_STATUS_COLORS[statuses.length % DEFAULT_STATUS_COLORS.length];
    updateNested("statuses", [
      ...statuses,
      createEmptyStatus(`Status ${statuses.length + 1}`, color),
    ]);
  };

  const updateStatus = (statusId, patch) => {
    updateNested(
      "statuses",
      (tracker.statuses || []).map((status) =>
        status.id === statusId ? { ...status, ...patch } : status
      )
    );
  };

  const removeStatus = (statusId) => {
    updateNested(
      "statuses",
      (tracker.statuses || []).filter((status) => status.id !== statusId)
    );
  };

  const updateSchedule = (patch) => {
    updateNested("submissionSchedule", {
      ...(tracker.submissionSchedule || {}),
      ...patch,
    });
  };

  const updateReviewPointSettings = (nextPoints) => {
    const points = Array.isArray(nextPoints) ? nextPoints : [];
    setTracker((prev) =>
      prev
        ? {
            ...prev,
            reviewPointSettings: points,
            submissionSchedule:
              buildSubmissionScheduleFromReviewPoints(points),
          }
        : prev
    );
  };

  const updateReviewPoint = (key, patch) => {
    const list = syncReviewPointSettings(
      tracker.createSetup || createDefaultJourneyCreateSetup(),
      tracker.reviewPointSettings
    );
    updateReviewPointSettings(
      list.map((item) => {
        if (item.key !== key) return item;
        const next = { ...item, ...patch };
        if (patch.amount != null || patch.unit || patch.label != null) {
          next.label = formatReviewPointLabel(next);
        }
        return next;
      })
    );
  };

  const addCustomReviewPoint = () => {
    const amount = Number(customPointAmount) || 0;
    if (amount < 1) {
      enqueueSnackbar("Enter a number greater than 0", { variant: "warning" });
      return false;
    }
    // Unit follows the check-in frequency on Basics.
    const unit = getSchedulePointUnit(tracker.createSetup);
    const list = syncReviewPointSettings(
      tracker.createSetup || createDefaultJourneyCreateSetup(),
      tracker.reviewPointSettings
    );
    const duplicate = list.some(
      (item) =>
        Number(item.amount) === amount && (item.unit || "weeks") === unit
    );
    if (duplicate) {
      enqueueSnackbar("That schedule point already exists", {
        variant: "info",
      });
      return false;
    }
    const point = createEmptyReviewPointSetting({
      key: `custom_${unit}_${amount}_${Date.now()}`,
      amount,
      unit,
      label: formatReviewPointLabel({ amount, unit }),
    });
    updateReviewPointSettings([...list, point]);
    setEditingReviewPointKey(point.key);
    return true;
  };

  // Popup Add: closes only when the point was added.
  const handleAddSchedulePoint = () => {
    if (addCustomReviewPoint()) setAddPointOpen(false);
  };

  const removeReviewPoint = (key) => {
    const list = syncReviewPointSettings(
      tracker.createSetup || createDefaultJourneyCreateSetup(),
      tracker.reviewPointSettings
    );
    if (editingReviewPointKey === key) setEditingReviewPointKey(null);
    updateReviewPointSettings(list.filter((item) => item.key !== key));
  };

  const createBlankExtraQuestion = () => ({
    id: `extra_${Date.now()}`,
    label: "",
    fieldType: "short_text",
    required: false,
    options: [],
    source: "tracker",
    questionId: null,
  });

  // Opening a point with no extras shows one empty field straight away;
  // closing it drops extras that were left empty.
  const toggleReviewPointExtras = (pointKey) => {
    const closingKey = editingReviewPointKey;
    const opening = closingKey !== pointKey;
    const list = syncReviewPointSettings(
      tracker.createSetup || createDefaultJourneyCreateSetup(),
      tracker.reviewPointSettings
    );
    updateReviewPointSettings(
      list.map((item) => {
        let extras = item.extraQuestions || [];
        if (item.key === closingKey) {
          extras = extras.filter((q) => String(q.label || "").trim());
        }
        if (opening && item.key === pointKey && extras.length === 0) {
          extras = [createBlankExtraQuestion()];
        }
        return { ...item, extraQuestions: extras };
      })
    );
    setEditingReviewPointKey(opening ? pointKey : null);
  };

  const scheduleId = (prefix) =>
    `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

  const addRelativeOffset = () => {
    const list = tracker.submissionSchedule?.relativeOffsets || [];
    const last = list[list.length - 1];
    updateSchedule({
      relativeOffsets: [
        ...list,
        {
          id: scheduleId("rel"),
          label: "",
          amount: last ? Number(last.amount || 0) + 30 : 30,
          unit: last?.unit || "days",
        },
      ],
    });
  };

  const updateRelativeOffset = (id, patch) => {
    updateSchedule({
      relativeOffsets: (tracker.submissionSchedule?.relativeOffsets || []).map(
        (item) => (item.id === id ? { ...item, ...patch } : item)
      ),
    });
  };

  const removeRelativeOffset = (id) => {
    updateSchedule({
      relativeOffsets: (tracker.submissionSchedule?.relativeOffsets || []).filter(
        (item) => item.id !== id
      ),
    });
  };

  const addCalendarDate = () => {
    const list = tracker.submissionSchedule?.calendarDates || [];
    updateSchedule({
      calendarDates: [
        ...list,
        {
          id: scheduleId("cal"),
          label: "",
          month: 1,
          day: 1,
        },
      ],
    });
  };

  const updateCalendarDate = (id, patch) => {
    updateSchedule({
      calendarDates: (tracker.submissionSchedule?.calendarDates || []).map(
        (item) => (item.id === id ? { ...item, ...patch } : item)
      ),
    });
  };

  const removeCalendarDate = (id) => {
    updateSchedule({
      calendarDates: (tracker.submissionSchedule?.calendarDates || []).filter(
        (item) => item.id !== id
      ),
    });
  };

  const addCustomPoint = () => {
    const list = tracker.submissionSchedule?.customPoints || [];
    const unit = tracker.submissionSchedule?.customUnit || "days";
    const last = list[list.length - 1];
    const nextValue = last ? Number(last.value || 0) + (unit === "weeks" ? 4 : 30) : unit === "weeks" ? 1 : 30;
    updateSchedule({
      customPoints: [
        ...list,
        {
          id: scheduleId("cus"),
          name: unit === "weeks" ? `Week ${nextValue}` : `Day ${nextValue}`,
          value: nextValue,
        },
      ],
    });
  };

  const updateCustomPoint = (id, patch) => {
    updateSchedule({
      customPoints: (tracker.submissionSchedule?.customPoints || []).map(
        (item) => (item.id === id ? { ...item, ...patch } : item)
      ),
    });
  };

  const removeCustomPoint = (id) => {
    updateSchedule({
      customPoints: (tracker.submissionSchedule?.customPoints || []).filter(
        (item) => item.id !== id
      ),
    });
  };

  const coachSelectOptions = useMemo(() => {
    const byId = new Map();
    coachRoster.forEach((coach) => {
      if (coach?.id != null) byId.set(coach.id, coach);
    });
    (tracker?.coaches || []).forEach((coach) => {
      if (coach?.id != null && !byId.has(coach.id)) byId.set(coach.id, coach);
    });
    return Array.from(byId.values());
  }, [coachRoster, tracker?.coaches]);

  if (pageLoading || !tracker) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress
          size={36}
          sx={{ color: "var(--portal-theme-primary, #04abbb)" }}
        />
      </Box>
    );
  }

  const isLeadCapture =
    intent === "lead" || isLeadCaptureTracker(tracker);
  const showCoachWorkflow = !isLeadCapture;

  const forms = tracker.forms || [];
  const sharedQuestions = tracker.sharedQuestions || [];
  const measurableQuestions = tracker.sharedQuestions.filter((question) => question.measurable === true);
  const generalQuestions = tracker.sharedQuestions.filter((question) => question.measurable !== true);
  const statuses = tracker.statuses || [];
  const tags = tracker.tags || [];
  const programmes = tracker.programmes || [];
  const trackerAccess = normalizeTrackerAccess(
    tracker.trackerAccess,
    tracker
  );
  const programmeSelectOptions = programmeOptions;
  const reviewSchedule = tracker.submissionSchedule || {};
  const reviewType =
    reviewSchedule.type || REVIEW_SCHEDULE_TYPES.RELATIVE;
  const relativeOffsets = reviewSchedule.relativeOffsets || [];
  const calendarDates = reviewSchedule.calendarDates || [];
  const customPoints = reviewSchedule.customPoints || [];
  const customUnit = reviewSchedule.customUnit || "days";
  // Distinct questions members answer — shared ones sit on every form but
  // count once (the Start / Check-in forms are internal).
  const questionCount = new Set(
    forms.flatMap((form) =>
      getEffectiveFormQuestions(form, sharedQuestions, [], forms).map(
        (q) => q.sharedQuestionId || q.id
      )
    )
  ).size;
  const isLive = tracker.status === "live" || tracker.status === "published";
  const isArchived = tracker.status === TRACKER_STATUS.ARCHIVED;
  const assignedCoaches = tracker.coaches || [];
  const permissionsCoach =
    assignedCoaches.find((c) => c.id === permissionsExpandedCoachId) ||
    null;
  const coachReview = tracker.coachReview || createDefaultCoachReview();
  const coachReviewQuestions = coachReview.questions || [];
  const isSeriesPattern = tracker.questionPattern === "different_forms_in_order";
  const allowMultipleForms = isSeriesPattern;
  const scheduleCheckpointCount = countSubmissionCheckpoints(reviewSchedule);
  const suggestedSeriesFormCount = 1 + scheduleCheckpointCount;
  const suggestedUnlockCount = Math.max(0, suggestedSeriesFormCount - 1);
  const formsMatchSchedule =
    !isSeriesPattern || forms.length === suggestedSeriesFormCount;

  const setup = tracker.createSetup || null;
  const durationOptions = (() => {
    const options = [...JOURNEY_DURATION_OPTIONS];
    if (!setup) return options;
    const current = setup.durationWeeks;
    if (
      typeof current === "number" &&
      !options.some((item) => item.value === current)
    ) {
      options.splice(options.length - 1, 0, {
        value: current,
        label: `${current} weeks`,
      });
    }
    return options;
  })();
  const coachReviewSetup = setup
    ? normalizeJourneyCoachReviewSetup(setup)
    : null;
  const scheduledReviews = coachReviewSetup
    ? normalizeScheduledReviews(coachReviewSetup)
    : "by_week";
  const alsoReviewEveryCheckin = coachReviewSetup
    ? normalizeAlsoReviewEveryCheckin(coachReviewSetup)
    : true;
  const selectedReviewWeeks = coachReviewSetup?.coachReviewWeeks || [];
  const selectedReviewDays = coachReviewSetup?.coachReviewDays || [];
  const fixedReviewDates = coachReviewSetup?.coachReviewFixedDates || [];
  const replyWithinDays = coachReviewSetup?.replyWithinDays ?? 2;
  const maxReviewWeek = coachReviewSetup
    ? getMaxReviewWeek(coachReviewSetup)
    : null;
  const maxReviewDay = coachReviewSetup
    ? getMaxReviewDay(coachReviewSetup)
    : null;
  const durationUnlimited = coachReviewSetup
    ? isJourneyDurationUnlimited(coachReviewSetup)
    : false;
  const reviewPointSettings = syncReviewPointSettings(
    setup || createDefaultJourneyCreateSetup(),
    tracker.reviewPointSettings
  );
  const scheduleNeedsViewAll =
    reviewPointSettings.length > REVIEW_POINT_VIEW_THRESHOLD;
  const visibleReviewPoints =
    scheduleNeedsViewAll && !schedulePointsExpanded
      ? reviewPointSettings.slice(0, REVIEW_POINT_VIEW_THRESHOLD)
      : reviewPointSettings;

  const formQuestionsOnly = forms.reduce(
    (sum, form) => sum + (form.questions || []).length,
    0
  );
  // Check-in answers come from personal info + shared library + optional form extras.
  // Requiring only form.questions left Save & continue disabled when shared/personal
  // were filled (Publish stayed enabled because it does not use this gate).
  const checkInQuestionCount =
    formQuestionsOnly +
    (sharedQuestions || []).length +
    (tracker.personalInfoQuestions || []).length;

  const isBasicsComplete = (() => {
    if (!tracker.name?.trim()) return false;
    if (
      normalizeTrackerAccess(tracker.trackerAccess, tracker) ===
        TRACKER_ACCESS.GYMRITE_MEMBERS &&
      !(tracker.programmes || []).length
    ) {
      return false;
    }
    const publishDate =
      setup?.publishDate || tracker.publishDate || null;
    if (!publishDate) return false;
    if (!setup) return true;
    const mode = normalizeCheckInMode(setup.checkInMode);
    if (mode === "weekly") {
      return (setup.checkInWeekdays || []).length > 0;
    }
    if (mode === "monthly") {
      return true;
    }
    if (mode === "custom") {
      return Number(setup.checkInEveryDays) > 0;
    }
    return true;
  })();

  // Lead capture: personal info is always present; extra questions are optional.
  // Journey: any personal/shared/form question is enough to save this tab.
  const isQuestionsComplete = isLeadCapture
    ? questionsVisited || !isCreate
    : checkInQuestionCount > 0 && (questionsVisited || !isCreate);
  const basicsTabDone = Boolean(savedTabs.basics);
  const questionsTabDone = Boolean(savedTabs.questions);
  const coachesTabDone = Boolean(savedTabs.coaches);
  const settingsTabDone = Boolean(savedTabs.settings);

  const handleBuilderTabChange = (_, value) => {
    if (
      (value === "questions" || value === "coaches" || value === "settings") &&
      !savedTabs.basics
    ) {
      enqueueSnackbar(
        "Save Basics first before opening the next tab.",
        { variant: "info" }
      );
      setBuilderTab("basics");
      return;
    }
    if ((value === "coaches" || value === "settings") && isLeadCapture) {
      enqueueSnackbar("Lead capture does not use these settings.", {
        variant: "info",
      });
      return;
    }
    if (
      (value === "coaches" || value === "settings") &&
      !savedTabs.questions
    ) {
      enqueueSnackbar(
        "Save Check-in Configuration before opening the next tab.",
        { variant: "info" }
      );
      setBuilderTab("questions");
      setQuestionsVisited(true);
      return;
    }
    if (value === "questions") {
      setQuestionsVisited(true);
    }
    setBuilderTab(value);
  };

  const TabLabel = ({ label, done }) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      {done ? (
        <CheckCircleIcon sx={{ fontSize: 17, color: PRIMARY }} />
      ) : null}
      <span>{label}</span>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div
        className="container-fluid tracker-module-page tracker-builder-page"
        style={isCreate ? { paddingBottom: 72 } : undefined}
      >
        <Box
          className="tracker-builder-header"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  navigate(
                    isCreate
                      ? "/trackers/new"
                      : location.state?.returnTo || `/trackers/${tracker.id}`
                  )
                }
                sx={{ p: 0.75 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography
                component="button"
                type="button"
                onClick={() => navigate("/trackers")}
                sx={{
                  border: 0,
                  background: "none",
                  p: 0,
                  cursor: "pointer",
                  color: MUTED,
                  fontSize: 13,
                  "&:hover": { color: PRIMARY },
                }}
              >
                All trackers
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
              {isCreate ? (
                <>
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => navigate("/trackers/new")}
                    sx={{
                      border: 0,
                      background: "none",
                      p: 0,
                      cursor: "pointer",
                      color: MUTED,
                      fontSize: 13,
                      "&:hover": { color: PRIMARY },
                    }}
                  >
                    New
                  </Typography>
                  <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
                  <Typography
                    sx={{
                      color: "#0f172a",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {intent === "blank"
                      ? "Blank"
                      : intent === "lead"
                        ? "Lead Capture"
                        : "Journey"}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    component="button"
                    type="button"
                    onClick={() =>
                      navigate(
                        location.state?.returnTo || `/trackers/${tracker.id}`
                      )
                    }
                    sx={{
                      border: 0,
                      background: "none",
                      p: 0,
                      cursor: "pointer",
                      color: MUTED,
                      fontSize: 13,
                      maxWidth: 220,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&:hover": { color: PRIMARY },
                    }}
                  >
                    {tracker.name || "Untitled journey"}
                  </Typography>
                  <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
                  <Typography
                    sx={{
                      color: "#0f172a",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    Edit
                  </Typography>
                </>
              )}
              {!isCreate && (
                <Chip
                  size="small"
                  label={
                    isArchived ? "Archived" : isLive ? "Live" : "Draft"
                  }
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: 11,
                    bgcolor: isArchived
                      ? "#f1f5f9"
                      : isLive
                        ? PRIMARY_SOFT
                        : "#f1f5f9",
                    color: isArchived ? MUTED : isLive ? PRIMARY : MUTED,
                    border: `1px solid ${
                      isArchived ? BORDER : isLive ? PRIMARY : BORDER
                    }`,
                  }}
                />
              )}
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 24, md: 28 },
                color: "#0f172a",
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
              }}
            >
              {isCreate
                ? intent === "blank"
                  ? "New blank tracker"
                  : intent === "lead"
                    ? "New lead capture"
                    : "New journey tracker"
                : tracker.name ||
                  (isLeadCapture ? "Untitled lead capture" : "Untitled journey")}
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 13, color: MUTED }}>
              {isCreate
                ? isLeadCapture
                  ? "Fill Basics and save — then set Configuration (questions & coaches)."
                  : "Fill Basics and save — Check-in & Coaches open after that."
                : `${questionCount} question${
                    questionCount === 1 ? "" : "s"
                  } · edited ${formatEditedAgo(tracker.updatedAt)}`}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              flexShrink: 0,
              pt: { xs: 0, md: 0.5 },
            }}
          >
            {/* Publish lives only in the bottom bar, next to Save. */}
          </Box>
        </Box>

        {!isCreate && (
        <Tabs
          value={builderTab}
          onChange={handleBuilderTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            mb: 3,
            minHeight: 44,
            borderBottom: `1px solid ${BORDER}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              minHeight: 44,
              color: MUTED,
            },
            "& .Mui-selected": { color: `${PRIMARY} !important` },
            "& .MuiTabs-indicator": { backgroundColor: PRIMARY, height: 3 },
            "& .Mui-disabled": { opacity: 0.45 },
          }}
        >
          <Tab
            value="basics"
            label={<TabLabel label="Basics" done={basicsTabDone} />}
          />
          <Tab
            value="questions"
            disabled={!savedTabs.basics}
            label={
              <TabLabel
                label={isLeadCapture ? "Configuration" : "Check-in Configuration"}
                done={questionsTabDone}
              />
            }
          />
          {showCoachWorkflow && (
            <Tab
              value="coaches"
              disabled={!savedTabs.basics || !savedTabs.questions}
              label={
                <TabLabel
                  label="Coaches configurations"
                  done={coachesTabDone}
                />
              }
            />
          )}
          {showCoachWorkflow && (
            <Tab
              value="settings"
              disabled={!savedTabs.basics || !savedTabs.questions}
              label={
                <TabLabel
                  label="Check-in settings"
                  done={settingsTabDone}
                />
              }
            />
          )}
        </Tabs>
        )}

        <div className="row tracker-builder-layout">
          <div
            className="col-12 tracker-builder-main-col"
            style={
              builderTab === "basics" ||
              builderTab === "questions" ||
              builderTab === "coaches" ||
              builderTab === "settings" ||
              isCreate
                ? { paddingBottom: 72 }
                : undefined
            }
          >
            {(isCreate || builderTab === "basics") && (
              <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                mb: 2.5,
                width: "100%",
                pt: 0.75,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoOutlinedIcon
                        fontSize="small"
                        sx={{ color: PRIMARY }}
                      />

<Typography
                        sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}
                      >
                        General Info
                      </Typography>
                    </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2.5,
                  alignItems: "start",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  required
                  label={isLeadCapture ? "Tracker name" : "Journey name"}
                  value={tracker.name || ""}
                  onChange={(e) => update({ name: e.target.value })}
                  onBlur={() => setNameTouched(true)}
                  error={nameTouched && !tracker.name?.trim()}
                  helperText={
                    nameTouched && !tracker.name?.trim()
                      ? `${isLeadCapture ? "Tracker" : "Journey"} name is required`
                      : ""
                  }
                />
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Journey Access"
                  value={trackerAccess}
                  onChange={(e) => handleTrackerAccessChange(e.target.value)}
                >
                  {TRACKER_ACCESS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {trackerAccess === TRACKER_ACCESS.GYMRITE_MEMBERS && (
                <Autocomplete
                  multiple
                  size="small"
                  fullWidth
                  options={programmeSelectOptions}
                  loading={programmesLoading}
                  value={programmes}
                  onChange={(_, value) => handleProgrammeSelect(value)}
                  onBlur={() => setProgrammeTouched(true)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) => option.title || ""}
                  filterSelectedOptions
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.title}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        size="small"
                        label={option.title || ""}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Programme"
                      required
                      error={programmeTouched && !programmes.length}
                      helperText={
                        programmeTouched && !programmes.length
                          ? PROGRAMME_REQUIRED_MESSAGE
                          : ""
                      }
                      placeholder={
                        programmesLoading
                          ? "Loading…"
                          : "Select programme(s)"
                      }
                    />
                  )}
                />
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2.5,
                  alignItems: "start",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="Internal description"
                  value={tracker.description || ""}
                  onChange={(e) => update({ description: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <FieldInfoAdornment text="Only for your team. Shown in the admin and coach portals (journey details and search). Members never see it — not on the join page and not in their portal." />
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  label="What visitors see"
                  value={tracker.visitorMessage || ""}
                  onChange={(e) => update({ visitorMessage: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <FieldInfoAdornment text="Shown to members and visitors: the intro on the public join page, and the journey card and journey page in the member portal. If left empty, the join page shows: “A focused check-in so your coach can see your progress and guide what comes next.” The member portal then shows no intro text." />
                    ),
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 1fr",
                  },
                  gap: 2.5,
                  alignItems: "start",
                  pt: 0.5,
                }}
              >
                <DatePicker
                  label="Publish date"
                  value={
                    setup?.publishDate || tracker.publishDate
                      ? new Date(
                          `${setup?.publishDate || tracker.publishDate}T00:00:00`
                        )
                      : new Date()
                  }
                  onChange={(date) => {
                    const publishDate = date
                      ? moment(date).format("YYYY-MM-DD")
                      : getTodayDateString();
                    if (setup) {
                      updateCreateSetup({ publishDate });
                    } else {
                      update({ publishDate });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          bgcolor: "#fff",
                        },
                      }}
                      error={
                        !(setup?.publishDate || tracker.publishDate)
                      }
                      helperText={
                        !(setup?.publishDate || tracker.publishDate)
                          ? "Publish date is required"
                          : ""
                      }
                    />
                  )}
                />

                {!isLeadCapture && (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Journey start date"
                  value={normalizeJourneyStartsFrom(
                    setup?.journeyStartsFrom || tracker.journeyStartsFrom
                  )}
                  onChange={(e) => {
                    const journeyStartsFrom = e.target.value;
                    const patch = { journeyStartsFrom };
                    if (
                      journeyStartsFrom === "from_publish_date" &&
                      !(setup?.publishDate || tracker.publishDate)
                    ) {
                      patch.publishDate = getTodayDateString();
                    }
                    if (setup) {
                      updateCreateSetup(patch);
                    } else {
                      update(patch);
                    }
                  }}
                >
                  {JOURNEY_START_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                )}
              </Box>

              {setup && (
                <>
                  <Box sx={{ width: "100%", pt: 0.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        mb: 2,
                      }}
                    >
                      <ScheduleOutlinedIcon
                        fontSize="small"
                        sx={{ color: PRIMARY }}
                      />
                      <Typography
                        sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}
                      >
                        How long?
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md:
                            setup.durationWeeks === "custom"
                              ? "repeat(13, minmax(0, 1fr))"
                              : "repeat(12, minmax(0, 1fr))",
                        },
                        gap: 2.5,
                        alignItems: "start",
                        width: "100%",
                        mb: 3.5,
                      }}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Duration"
                        value={
                          setup.durationWeeks === "ongoing"
                            ? "always_open"
                            : "open_for_period"
                        }
                        onChange={(e) => {
                          if (e.target.value === "always_open") {
                            updateCreateSetup({ durationWeeks: "ongoing" });
                            return;
                          }
                          const current = setup.durationWeeks;
                          // The admin picks the weeks (no preset period).
                          updateCreateSetup({
                            durationWeeks:
                              current === "ongoing" || current == null
                                ? ""
                                : current,
                          });
                        }}
                        sx={{
                          gridColumn: { xs: "1 / -1", md: "span 6" },
                        }}
                      >
                        <MenuItem value="always_open">
                          Ongoing — no end
                        </MenuItem>
                        <MenuItem value="open_for_period">
                          Open for a period
                        </MenuItem>
                      </TextField>

                      {setup.durationWeeks !== "ongoing" && (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={
                            "Duration Period"
                          }
                          required
                          error={durationTouched && setup.durationWeeks === ""}
                          helperText={
                            durationTouched && setup.durationWeeks === ""
                              ? "Choose how long the journey runs"
                              : undefined
                          }
                          value={setup.durationWeeks}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "custom") {
                              updateCreateSetup({ durationWeeks: "custom" });
                              return;
                            }
                            updateCreateSetup({
                              durationWeeks: Number(value),
                            });
                          }}
                          sx={{
                            gridColumn: {
                              xs: "1 / -1",
                              md:
                                setup.durationWeeks === "custom"
                                  ? "span 4"
                                  : "span 6",
                            },
                          }}
                        >
                          {durationOptions
                            .filter((option) => option.value !== "ongoing")
                            .map((option) => (
                              <MenuItem
                                key={String(option.value)}
                                value={option.value}
                              >
                                {option.value === "custom"
                                  ? "Custom"
                                  : option.label}
                              </MenuItem>
                            ))}
                        </TextField>
                      )}

                      {setup.durationWeeks === "custom" && (
                        <TextField
                          fullWidth
                          type="number"
                          size="small"
                          label="Days"
                          value={setup.customDurationDays}
                          onChange={(e) =>
                            updateCreateSetup({
                              customDurationDays: Number(e.target.value) || 0,
                            })
                          }
                          inputProps={{ min: 1 }}
                          sx={{
                            gridColumn: { xs: "1 / -1", md: "span 3" },
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        mb: 2,
                      }}
                    >
                      <ScheduleOutlinedIcon
                        fontSize="small"
                        sx={{ color: PRIMARY }}
                      />
                      <Typography
                        sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}
                      >
                        How often does the member check in?
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: (() => {
                          const mode = normalizeCheckInMode(setup.checkInMode);
                          if (mode === "monthly") {
                            const style = normalizeCheckInMonthlyStyle(
                              setup.checkInMonthlyStyle
                            );
                            if (style === "on_the_week_and_day") {
                              return {
                                xs: "1fr",
                                md: "repeat(4, minmax(0, 1fr))",
                              };
                            }
                            return {
                              xs: "1fr",
                              md: "repeat(3, minmax(0, 1fr))",
                            };
                          }
                          if (mode === "weekly") {
                            return {
                              xs: "1fr",
                              md: "minmax(180px, 220px) repeat(7, minmax(0, 1fr))",
                            };
                          }
                          return {
                            xs: "1fr",
                            md: "minmax(200px, 240px) minmax(0, 1fr)",
                          };
                        })(),
                        gap: 2.5,
                        alignItems: "start",
                        width: "100%",
                      }}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Frequency"
                        value={normalizeCheckInMode(setup.checkInMode)}
                        onChange={(e) => {
                          const checkInMode = e.target.value;
                          const patch = { checkInMode };
                          if (checkInMode === "monthly") {
                            patch.checkInMonthlyStyle =
                              normalizeCheckInMonthlyStyle(
                                setup.checkInMonthlyStyle
                              );
                            patch.checkInMonthDay = setup.checkInMonthDay ?? 1;
                            patch.checkInMonthWeekOrdinal =
                              setup.checkInMonthWeekOrdinal || "first";
                            patch.checkInMonthWeekday =
                              setup.checkInMonthWeekday || "monday";
                          }
                          updateCreateSetup(patch);
                        }}
                      >
                        {MEMBER_CHECKIN_MODE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.title}
                          </MenuItem>
                        ))}
                      </TextField>

                      {normalizeCheckInMode(setup.checkInMode) ===
                        "weekly" && (
                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                            display: { xs: "grid", md: "contents" },
                            gridTemplateColumns: {
                              xs: "repeat(7, minmax(0, 1fr))",
                            },
                            gap: 0.75,
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          {WEEKDAY_OPTIONS.map((day) => {
                            const selected = (
                              setup.checkInWeekdays || []
                            ).includes(day.value);
                            return (
                              <Box
                                key={day.value}
                                onClick={() => {
                                  const set = new Set(
                                    setup.checkInWeekdays || []
                                  );
                                  if (set.has(day.value)) set.delete(day.value);
                                  else set.add(day.value);
                                  const next = WEEKDAY_OPTIONS.map(
                                    (item) => item.value
                                  ).filter((value) => set.has(value));
                                  updateCreateSetup({
                                    checkInWeekdays: next.length
                                      ? next
                                      : [day.value],
                                  });
                                }}
                                sx={{
                                  textAlign: "center",
                                  height: 44,
                                  minHeight: 44,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "8px",
                                  border: "1.5px solid",
                                  borderColor: selected ? PRIMARY : BORDER,
                                  bgcolor: selected ? PRIMARY_SOFT : "#fff",
                                  color: selected ? "#0f172a" : "#334155",
                                  fontWeight: 600,
                                  fontSize: 12,
                                  cursor: "pointer",
                                  boxShadow: selected
                                    ? `0 0 0 1px ${PRIMARY}`
                                    : "none",
                                  userSelect: "none",
                                }}
                              >
                                {day.label}
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {normalizeCheckInMode(setup.checkInMode) ===
                        "weekly" && (
                        <Box
                          sx={{
                            gridColumn: "1 / -1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.25,
                            px: 1.5,
                            py: 1.1,
                            borderRadius: "10px",
                            border: `1px solid ${BORDER}`,
                            bgcolor: "#fff",
                            minHeight: 56,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 650, fontSize: 13.5 }}>
                              Spread check-in days across the week
                            </Typography>
                            <Typography
                              sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}
                            >
                              {(setup.checkInWeekdays || []).length < 2
                                ? "Select 2 or more days above first. With one day, everyone stays on that day (Spread does nothing)."
                                : tracker.settings?.spreadCheckInDays === true
                                  ? "On: each new member gets the least-busy day from the days selected above. They get an email with their day."
                                  : "Off: every member follows all selected days — same as before."}
                            </Typography>
                          </Box>
                          <Switch
                            size="small"
                            checked={
                              tracker.settings?.spreadCheckInDays === true
                            }
                            onChange={(e) =>
                              updateSettings({
                                spreadCheckInDays: e.target.checked,
                              })
                            }
                            sx={switchSx}
                            inputProps={{
                              "aria-label": "Spread check-in days",
                            }}
                          />
                        </Box>
                      )}

                      {normalizeCheckInMode(setup.checkInMode) ===
                        "monthly" && (
                        <>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Monthly"
                            value={normalizeCheckInMonthlyStyle(
                              setup.checkInMonthlyStyle
                            )}
                            onChange={(e) =>
                              updateCreateSetup({
                                checkInMonthlyStyle: e.target.value,
                              })
                            }
                            sx={{
                            }}
                          >
                            {MONTHLY_OPTIONS.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.title}
                              </MenuItem>
                            ))}
                          </TextField>
                          {normalizeCheckInMonthlyStyle(
                            setup.checkInMonthlyStyle
                          ) === "on_this_date" ? (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Day of month"
                              value={setup.checkInMonthDay ?? 1}
                              onChange={(e) =>
                                updateCreateSetup({
                                  checkInMonthDay: Number(e.target.value),
                                })
                              }
                              sx={{
                              }}
                            >
                              {MONTH_DAY_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Week"
                                value={normalizeCheckInMonthWeekOrdinal(
                                  setup.checkInMonthWeekOrdinal
                                )}
                                onChange={(e) =>
                                  updateCreateSetup({
                                    checkInMonthWeekOrdinal: e.target.value,
                                  })
                                }
                                sx={{
                                }}
                              >
                                {WEEK_AND_DAY_OPTIONS.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Day"
                                value={normalizeCheckInMonthWeekday(
                                  setup.checkInMonthWeekday
                                )}
                                onChange={(e) =>
                                  updateCreateSetup({
                                    checkInMonthWeekday: e.target.value,
                                  })
                                }
                                sx={{
                                }}
                              >
                                {WEEK_DAY_OPTIONS.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </>
                          )}
                        </>
                      )}

                      {normalizeCheckInMode(setup.checkInMode) ===
                        "custom" && (
                        <TextField
                          type="number"
                          size="small"
                          label="Every how many days?"
                          value={setup.checkInEveryDays}
                          onChange={(e) =>
                            updateCreateSetup({
                              checkInEveryDays: Number(e.target.value) || 0,
                            })
                          }
                          inputProps={{ min: 1 }}
                          sx={{ maxWidth: 200 }}
                        />
                      )}
                    </Box>
                  </Box>

                </>
              )}
            </Box>

            {/* {!isCreate && builderTab === "basics" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mb: 1,
                }}
              >
                <button
                  type="button"
                  className="small-contained-button"
                  disabled={!isBasicsComplete}
                  style={
                    !isBasicsComplete
                      ? { opacity: 0.55, cursor: "not-allowed" }
                      : undefined
                  }
                  onClick={handleSave}
                >
                  Save & continue
                </button>
              </Box>
            )} */}
              </>
            )}

            {!isCreate && builderTab === "questions" && isLeadCapture && (
              <>
                <SectionCard
                  icon={<QuestionAnswerOutlinedIcon fontSize="small" />}
                  title="General Questions"
                  gap={1}
                  description="Optional — add questions beyond the default lead form."
                  action={
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="small-contained-outlined-button"
                        onClick={() => {
                          setSharedLibraryTab("general");
                          setSharedLibrarySearch("");
                          setSelectedSharedLibraryIds([]);
                          setSharedLibraryOpen(true);
                        }}
                      >
                        From library
                      </button>
                      <button
                        type="button"
                        className="small-contained-outlined-button"
                        onClick={addSharedSpecificQuestion}
                      >
                        Add specific
                      </button>
                    </Box>
                  }
                >
                  {sharedQuestions.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: MUTED }}>
                      No extra questions yet. Add from the library or create a
                      specific question if you need more.
                    </Typography>
                  ) : (
                    sharedQuestions.map((question) => {
                      if (question.measurable) {
                        return null;
                      }
                      const fromLibrary =
                        question.source === FORM_QUESTION_SOURCES.LIBRARY ||
                        Boolean(question.questionId);
                      const libraryQuestion =
                        fromLibrary && question.questionId
                          ? libraryForShared.find(
                              (item) => item.id === question.questionId
                            )
                          : null;
                      const placeholderValue =
                        question.placeholder ||
                        libraryQuestion?.placeholder ||
                        "";
                      const isMeasurable =
                        question.measurable ||
                        question.mode === QUESTION_MODES.MEASURABLE ||
                        libraryQuestion?.mode === QUESTION_MODES.MEASURABLE;
                      const typeDisplay = isMeasurable
                        ? getMeasureTypeLabel(
                            question.measureType ||
                              libraryQuestion?.measureType
                          )
                        : getFieldTypeLabel(
                            question.fieldType || libraryQuestion?.fieldType
                          );
                      return (
                        <Box
                          key={question.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr auto auto",
                              md: fromLibrary
                                ? "minmax(0, 1fr) auto auto auto"
                                : "minmax(0, 1.15fr) minmax(0, 0.95fr) minmax(140px, 0.75fr) auto auto",
                            },
                            gap: { xs: 1.5, md: 2 },
                            alignItems: "center",
                            pt: 0.5,
                            width: "100%",
                          }}
                        >
                          {fromLibrary ? (
                            <Box sx={{ minWidth: 0, py: 0.5 }}>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: "#0f172a",
                                  lineHeight: 1.35,
                                }}
                              >
                                {question.label || "Untitled question"}
                              </Typography>
                              {placeholderValue ? (
                                <Typography
                                  sx={{
                                    mt: 0.35,
                                    fontSize: 12.5,
                                    color: MUTED,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {placeholderValue}
                                </Typography>
                              ) : null}
                            </Box>
                          ) : (
                            <>
                              <TextField
                                fullWidth
                                size="small"
                                label="Question statement"
                                value={question.label || ""}
                                onChange={(e) =>
                                  updateSharedQuestion(question.id, {
                                    label: e.target.value,
                                  })
                                }
                                sx={{
                                  gridColumn: { xs: "1 / -1", md: "auto" },
                                }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Placeholder"
                                value={placeholderValue}
                                onChange={(e) =>
                                  updateSharedQuestion(question.id, {
                                    placeholder: e.target.value,
                                  })
                                }
                                sx={{
                                  gridColumn: { xs: "1 / -1", md: "auto" },
                                }}
                              />
                            </>
                          )}
                          {fromLibrary ? (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                flexShrink: 0,
                              }}
                              title={`${isMeasurable ? "Measurable" : "General"} · ${typeDisplay}`}
                            >
                              {/* <Chip
                                size="small"
                                label={isMeasurable ? "Measurable" : "General"}
                                sx={{
                                  height: 24,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  bgcolor: PRIMARY_SOFT,
                                  color: PRIMARY,
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12.5,
                                  fontWeight: 600,
                                  color: MUTED,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {typeDisplay}
                              </Typography> */}
                            </Box>
                          ) : (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Type"
                              value={question.fieldType || "short_text"}
                              onChange={(e) =>
                                updateSharedQuestion(question.id, {
                                  fieldType: e.target.value,
                                })
                              }
                            >
                              {QUESTION_FIELD_TYPES.filter(
                                (option) =>
                                  ![
                                    "status",
                                    "coach",
                                    "gym",
                                    "programme",
                                    "file_upload",
                                  ].includes(option.value)
                              ).map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(question.required)}
                                onChange={(e) =>
                                  updateSharedQuestion(question.id, {
                                    required: e.target.checked,
                                  })
                                }
                                sx={{
                                  color: PRIMARY,
                                  "&.Mui-checked": { color: PRIMARY },
                                }}
                              />
                            }
                            label="Required"
                            sx={{
                              m: 0,
                              flexShrink: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: 13,
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeSharedQuestion(question.id)}
                            sx={{
                              flexShrink: 0,
                              color: "#dc2626",
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })
                  )}
                </SectionCard>
                <SectionCard
                  icon={<QuestionAnswerOutlinedIcon fontSize="small" />}
                  title="Measurable Questions"
                  gap={1}
                  description="Optional — add questions beyond the default lead form."
                  action={
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="small-contained-outlined-button"
                        onClick={() => {
                          setSharedLibraryTab("measurable");
                          setSharedLibrarySearch("");
                          setSelectedSharedLibraryIds([]);
                          setSharedLibraryOpen(true);
                        }}
                      >
                        From library
                      </button>
                    </Box>
                  }
                >
                  {sharedQuestions.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: MUTED }}>
                      No extra questions yet. Add from the library or create a
                      specific question if you need more.
                    </Typography>
                  ) : (
                    sharedQuestions.map((question) => {
                      if (!question.measurable) {
                        return null;
                      }
                      const fromLibrary =
                        question.source === FORM_QUESTION_SOURCES.LIBRARY ||
                        Boolean(question.questionId);
                      const libraryQuestion =
                        fromLibrary && question.questionId
                          ? libraryForShared.find(
                              (item) => item.id === question.questionId
                            )
                          : null;
                      const placeholderValue =
                        question.placeholder ||
                        libraryQuestion?.placeholder ||
                        "";
                      const isMeasurable =
                        question.measurable ||
                        question.mode === QUESTION_MODES.MEASURABLE ||
                        libraryQuestion?.mode === QUESTION_MODES.MEASURABLE;
                      const typeDisplay = isMeasurable
                        ? getMeasureTypeLabel(
                            question.measureType ||
                              libraryQuestion?.measureType
                          )
                        : getFieldTypeLabel(
                            question.fieldType || libraryQuestion?.fieldType
                          );
                      return (
                        <Box
                          key={question.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr auto auto",
                              md: fromLibrary
                                ? "minmax(0, 1fr) auto auto auto"
                                : "minmax(0, 1.15fr) minmax(0, 0.95fr) minmax(140px, 0.75fr) auto auto",
                            },
                            gap: { xs: 1.5, md: 2 },
                            alignItems: "center",
                            pt: 0.5,
                            width: "100%",
                          }}
                        >
                          {fromLibrary ? (
                            <Box sx={{ minWidth: 0, py: 0.5 }}>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: "#0f172a",
                                  lineHeight: 1.35,
                                }}
                              >
                                {question.label || "Untitled question"}
                              </Typography>
                              {placeholderValue ? (
                                <Typography
                                  sx={{
                                    mt: 0.35,
                                    fontSize: 12.5,
                                    color: MUTED,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {placeholderValue}
                                </Typography>
                              ) : null}
                            </Box>
                          ) : (
                            <>
                              <TextField
                                fullWidth
                                size="small"
                                label="Question statement"
                                value={question.label || ""}
                                onChange={(e) =>
                                  updateSharedQuestion(question.id, {
                                    label: e.target.value,
                                  })
                                }
                                sx={{
                                  gridColumn: { xs: "1 / -1", md: "auto" },
                                }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Placeholder"
                                value={placeholderValue}
                                onChange={(e) =>
                                  updateSharedQuestion(question.id, {
                                    placeholder: e.target.value,
                                  })
                                }
                                sx={{
                                  gridColumn: { xs: "1 / -1", md: "auto" },
                                }}
                              />
                            </>
                          )}
                          {fromLibrary ? (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                flexShrink: 0,
                              }}
                              title={`${isMeasurable ? "Measurable" : "General"} · ${typeDisplay}`}
                            >
                              {/* <Chip
                                size="small"
                                label={isMeasurable ? "Measurable" : "General"}
                                sx={{
                                  height: 24,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  bgcolor: PRIMARY_SOFT,
                                  color: PRIMARY,
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12.5,
                                  fontWeight: 600,
                                  color: MUTED,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {typeDisplay}
                              </Typography> */}
                            </Box>
                          ) : (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Type"
                              value={question.fieldType || "short_text"}
                              onChange={(e) =>
                                updateSharedQuestion(question.id, {
                                  fieldType: e.target.value,
                                })
                              }
                            >
                              {QUESTION_FIELD_TYPES.filter(
                                (option) =>
                                  ![
                                    "status",
                                    "coach",
                                    "gym",
                                    "programme",
                                    "file_upload",
                                  ].includes(option.value)
                              ).map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(question.required)}
                                onChange={(e) =>
                                  updateSharedQuestion(question.id, {
                                    required: e.target.checked,
                                  })
                                }
                                sx={{
                                  color: PRIMARY,
                                  "&.Mui-checked": { color: PRIMARY },
                                }}
                              />
                            }
                            label="Required"
                            sx={{
                              m: 0,
                              flexShrink: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: 13,
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeSharedQuestion(question.id)}
                            sx={{
                              flexShrink: 0,
                              color: "#dc2626",
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })
                  )}
                </SectionCard>

                <SectionCard
                  icon={<GroupsOutlinedIcon fontSize="small" />}
                  title="Coaches"
                  description="Who should follow up on these leads."
                >
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={coachSelectOptions}
                    loading={coachesLoading}
                    value={assignedCoaches}
                    onChange={(_, value) => handleAssignedCoachesChange(value)}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) => coachDisplayName(option)}
                    filterSelectedOptions
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 10,
                              fontWeight: 700,
                              bgcolor: coachAvatarColor(option),
                            }}
                          >
                            {coachInitials(option)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 13.5,
                                fontWeight: 500,
                                color: "#0f172a",
                              }}
                            >
                              {coachDisplayName(option)}
                            </Typography>
                            {option.email ? (
                              <Typography
                                sx={{ fontSize: 11.5, color: MUTED }}
                              >
                                {option.email}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </li>
                    )}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select coaches"
                        placeholder={
                          coachesLoading
                            ? "Loading…"
                            : assignedCoaches.length
                              ? "Add more…"
                              : "Search and select coaches"
                        }
                      />
                    )}
                  />

                  {assignedCoaches.length ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                        mt: 1.5,
                      }}
                    >
                      {assignedCoaches.map((coach) => (
                        <Box
                          key={coach.id}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1,
                            py: 0.6,
                            borderRadius: "999px",
                            border: `1px solid ${BORDER}`,
                            bgcolor: "#fff",
                            maxWidth: "100%",
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 10,
                              fontWeight: 700,
                              bgcolor: coachAvatarColor(coach),
                            }}
                          >
                            {coachInitials(coach)}
                          </Avatar>
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              fontWeight: 500,
                              color: "#0f172a",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 160,
                            }}
                          >
                            {coachDisplayName(coach)}
                          </Typography>
                          <IconButton
                            size="small"
                            title="Remove"
                            onClick={() =>
                              handleAssignedCoachesChange(
                                assignedCoaches.filter((c) => c.id !== coach.id)
                              )
                            }
                            sx={{
                              p: 0.35,
                              color: MUTED,
                              "&:hover": { color: "#dc2626" },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ mt: 1.25, fontSize: 13, color: MUTED }}>
                      Optional — assign coaches who will call or follow up on
                      new leads.
                    </Typography>
                  )}
                </SectionCard>

                <SectionCard
                  icon={<LocalOfferOutlinedIcon fontSize="small" />}
                  title="Tags"
                  description="Available on submissions for filters and automations. Not auto-applied when someone joins — assign on the submission or via automation."
                >
                  <TrackerLibraryTagPicker
                    value={tags}
                    onChange={(next) => updateNested("tags", next)}
                  />
                </SectionCard>

                <SectionCard
                  icon={<LayersOutlinedIcon fontSize="small" />}
                  title="Publish settings"
                >
                  <PublishSettingsDrawer
                    variant="inline"
                    hideHeader
                    tracker={tracker}
                    publicUrl={publicUrl}
                    onChange={handlePublishSettingsChange}
                    onPublish={handlePublish}
                    publishing={isSaving}
                    onOpenQr={() => setQrPopupOpen(true)}
                  />
                </SectionCard>

                <Box sx={{ height: 72 }} />
              </>
            )}

            {!isCreate && builderTab === "questions" && !isLeadCapture && (
              <>
            <SectionCard
              icon={<QuestionAnswerOutlinedIcon fontSize="small" />}
              title="General Questions"
              gap={1}
              description=""
              action={
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="small-contained-outlined-button"
                    onClick={() => {
                      setSharedLibraryTab("general");
                      setSharedLibrarySearch("");
                      setSelectedSharedLibraryIds([]);
                      setSharedLibraryOpen(true);
                      // setSharedLibraryFilter ({ measurable: false });
                    }}
                  >
                    From library
                  </button>
                  <button
                    type="button"
                    className="small-contained-outlined-button"
                    onClick={addSharedSpecificQuestion}
                  >
                    Add specific
                  </button>
                </Box>
              }
            >
              {sharedQuestions.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  No general questions yet. Add from the library or create a specific question for this tracker.
                </Typography>
              ) : (
                generalQuestions.map((question) => {
                  const fromLibrary =
                    question.source === FORM_QUESTION_SOURCES.LIBRARY ||
                    Boolean(question.questionId);
                  const libraryQuestion =
                    fromLibrary && question.questionId
                      ? libraryForShared.find(
                          (item) => item.id === question.questionId
                        )
                      : null;
                  const placeholderValue =
                    question.placeholder ||
                    libraryQuestion?.placeholder ||
                    "";
                  const isMeasurable =
                    question.measurable ||
                    question.mode === QUESTION_MODES.MEASURABLE ||
                    libraryQuestion?.mode === QUESTION_MODES.MEASURABLE;
                  const typeDisplay = isMeasurable
                    ? getMeasureTypeLabel(
                        question.measureType || libraryQuestion?.measureType
                      )
                    : getFieldTypeLabel(
                        question.fieldType || libraryQuestion?.fieldType
                      );
                  return (
                    <Box
                      key={question.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr auto auto",
                          md: fromLibrary
                            ? "minmax(0, 1fr) auto auto auto"
                            : "minmax(0, 1.15fr) minmax(0, 0.95fr) minmax(140px, 0.75fr) auto auto",
                        },
                        gap: { xs: 1.5, md: 2 },
                        alignItems: "center",
                        pt: 0.5,
                        width: "100%",
                      }}
                    >
                      {fromLibrary ? (
                        <Box sx={{ minWidth: 0, py: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: "#0f172a",
                              lineHeight: 1.35,
                            }}
                          >
                            {question.label || "Untitled question"}
                          </Typography>
                          {placeholderValue ? (
                            <Typography
                              sx={{
                                mt: 0.35,
                                fontSize: 12.5,
                                color: MUTED,
                                lineHeight: 1.35,
                              }}
                            >
                              {placeholderValue}
                            </Typography>
                          ) : null}
                        </Box>
                      ) : (
                        <>
                          <TextField
                            fullWidth
                            size="small"
                            label="Question statement"
                            value={question.label || ""}
                            onChange={(e) =>
                              updateSharedQuestion(question.id, {
                                label: e.target.value,
                              })
                            }
                            sx={{
                              gridColumn: { xs: "1 / -1", md: "auto" },
                            }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Placeholder"
                            value={placeholderValue}
                            onChange={(e) =>
                              updateSharedQuestion(question.id, {
                                placeholder: e.target.value,
                              })
                            }
                            sx={{
                              gridColumn: { xs: "1 / -1", md: "auto" },
                            }}
                          />
                        </>
                      )}
                      {fromLibrary ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            flexShrink: 0,
                          }}
                          // title={`${isMeasurable ? "Measurable" : "General"} · ${typeDisplay}`}
                        >
                          {/* <Chip
                            size="small"
                            // label={isMeasurable ? "Measurable" : "General"}
                            sx={{
                              height: 24,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: PRIMARY_SOFT,
                              color: PRIMARY,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          /> */}
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: MUTED,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typeDisplay}
                          </Typography>
                        </Box>
                      ) : (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Type"
                          value={question.fieldType || "short_text"}
                          onChange={(e) =>
                            updateSharedQuestion(question.id, {
                              fieldType: e.target.value,
                            })
                          }
                        >
                          {QUESTION_FIELD_TYPES.filter(
                            (option) =>
                              ![
                                "status",
                                "coach",
                                "gym",
                                "programme",
                                "file_upload",
                              ].includes(option.value)
                          ).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(question.required)}
                            onChange={(e) =>
                              updateSharedQuestion(question.id, {
                                required: e.target.checked,
                              })
                            }
                            sx={{
                              color: PRIMARY,
                              "&.Mui-checked": { color: PRIMARY },
                            }}
                          />
                        }
                        label="Required"
                        sx={{
                          m: 0,
                          flexShrink: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeSharedQuestion(question.id)}
                        sx={{
                          flexShrink: 0,
                          color: "#dc2626",
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })
              )}
            </SectionCard>
            <SectionCard
              icon={<QuestionAnswerOutlinedIcon fontSize="small" />}
              title="Measurable Questions"
              gap={1}
              description=""
              action={
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="small-contained-outlined-button"
                    onClick={() => {
                      setSharedLibraryTab("measurable");
                      setSharedLibrarySearch("");
                      setSelectedSharedLibraryIds([]);
                      setSharedLibraryOpen(true);
                      // setSharedLibraryFilter ({ measurable: true });
                    }}
                  >
                    From library
                  </button>
                </Box>
              }
            >
              {measurableQuestions.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  No measurable questions yet. Add from the library
                </Typography>
              ) : (
                measurableQuestions.map((question) => {
                  const fromLibrary =
                    question.source === FORM_QUESTION_SOURCES.LIBRARY ||
                    Boolean(question.questionId);
                  const libraryQuestion =
                    fromLibrary && question.questionId
                      ? libraryForShared.find(
                          (item) => item.id === question.questionId
                        )
                      : null;
                  const placeholderValue =
                    question.placeholder ||
                    libraryQuestion?.placeholder ||
                    "";
                  const isMeasurable =
                    question.measurable ||
                    question.mode === QUESTION_MODES.MEASURABLE ||
                    libraryQuestion?.mode === QUESTION_MODES.MEASURABLE;
                  const typeDisplay = isMeasurable
                    ? getMeasureTypeLabel(
                        question.measureType || libraryQuestion?.measureType
                      )
                    : getFieldTypeLabel(
                        question.fieldType || libraryQuestion?.fieldType
                      );
                  return (
                    <Box
                      key={question.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr auto auto",
                          md: fromLibrary
                            ? "minmax(0, 1fr) auto auto auto"
                            : "minmax(0, 1.15fr) minmax(0, 0.95fr) minmax(140px, 0.75fr) auto auto",
                        },
                        gap: { xs: 1.5, md: 2 },
                        alignItems: "center",
                        pt: 0.5,
                        width: "100%",
                      }}
                    >
                      {fromLibrary ? (
                        <Box sx={{ minWidth: 0, py: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: "#0f172a",
                              lineHeight: 1.35,
                            }}
                          >
                            {question.label || "Untitled question"}
                          </Typography>
                          {placeholderValue ? (
                            <Typography
                              sx={{
                                mt: 0.35,
                                fontSize: 12.5,
                                color: MUTED,
                                lineHeight: 1.35,
                              }}
                            >
                              {placeholderValue}
                            </Typography>
                          ) : null}
                        </Box>
                      ) : (
                        <>
                          <TextField
                            fullWidth
                            size="small"
                            label="Question statement"
                            value={question.label || ""}
                            onChange={(e) =>
                              updateSharedQuestion(question.id, {
                                label: e.target.value,
                              })
                            }
                            sx={{
                              gridColumn: { xs: "1 / -1", md: "auto" },
                            }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Placeholder"
                            value={placeholderValue}
                            onChange={(e) =>
                              updateSharedQuestion(question.id, {
                                placeholder: e.target.value,
                              })
                            }
                            sx={{
                              gridColumn: { xs: "1 / -1", md: "auto" },
                            }}
                          />
                        </>
                      )}
                      {fromLibrary ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            flexShrink: 0,
                          }}
                          title={`${isMeasurable ? "Measurable" : "General"} · ${typeDisplay}`}
                        >
                          {/* <Chip
                            size="small"
                            label={isMeasurable ? "Measurable" : "General"}
                            sx={{
                              height: 24,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: PRIMARY_SOFT,
                              color: PRIMARY,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: MUTED,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {typeDisplay}
                          </Typography> */}
                        </Box>
                      ) : (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Type"
                          value={question.fieldType || "short_text"}
                          onChange={(e) =>
                            updateSharedQuestion(question.id, {
                              fieldType: e.target.value,
                            })
                          }
                        >
                          {QUESTION_FIELD_TYPES.filter(
                            (option) =>
                              ![
                                "status",
                                "coach",
                                "gym",
                                "programme",
                                "file_upload",
                              ].includes(option.value)
                          ).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(question.required)}
                            onChange={(e) =>
                              updateSharedQuestion(question.id, {
                                required: e.target.checked,
                              })
                            }
                            sx={{
                              color: PRIMARY,
                              "&.Mui-checked": { color: PRIMARY },
                            }}
                          />
                        }
                        label="Required"
                        sx={{
                          m: 0,
                          flexShrink: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeSharedQuestion(question.id)}
                        sx={{
                          flexShrink: 0,
                          color: "#dc2626",
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })
              )}
            </SectionCard>

            <SectionCard
              icon={<ScheduleOutlinedIcon fontSize="small" />}
              title="Journey Submission scheduling"
              action={
                setup && durationUnlimited ? (
                  <button
                    type="button"
                    className="small-contained-outlined-button"
                    onClick={() => {
                      setCustomPointAmount("0");
                      setAddPointOpen(true);
                    }}
                  >
                    Add
                  </button>
                ) : null
              }
            >
              {reviewPointSettings.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {durationUnlimited
                    ? "No schedule points yet — click Add."
                    : "No schedule points yet. Set How long on Basics first."}
                </Typography>
              ) : (
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {visibleReviewPoints.map((point) => {
                    const extraCount = (point.extraQuestions || []).length;
                    const excludedCount = (
                      point.excludedSharedQuestionIds || []
                    ).length;
                    const sharedTotal = sharedQuestions.length;
                    const sharedActive = Math.max(
                      0,
                      sharedTotal - excludedCount
                    );
                    const parts = [
                      `${sharedActive} shared question${
                        sharedActive === 1 ? "" : "s"
                      }`,
                    ];
                    if (extraCount > 0) {
                      parts.push(
                        `${extraCount} extra question${
                          extraCount === 1 ? "" : "s"
                        }`
                      );
                    }
                    if (excludedCount > 0) {
                      parts.push(
                        `${excludedCount} excluded`
                      );
                    }
                    const meta = parts.join(" · ");
                    const expanded = editingReviewPointKey === point.key;
                    const scheduleFromPublish =
                      setup &&
                      normalizeJourneyStartsFrom(
                        setup.journeyStartsFrom || tracker.journeyStartsFrom
                      ) === "from_publish_date";
                    const publishDate =
                      setup?.publishDate || tracker.publishDate || null;
                    const pointDateYmd =
                      scheduleFromPublish && publishDate
                        ? getReviewPointDateFromPublish(publishDate, point)
                        : null;
                    // Show the whole window this point covers (same rule as
                    // the backend schedule_point_helpers.pointWindow): from the
                    // day after the previous point (or after publish) up to
                    // this point. A check-in on any day in the window counts
                    // for this week, so e.g. weekly-Thursday check-ins are
                    // visibly inside "Week 1 · Oct 15 – Oct 21".
                    let pointDateLabel = null;
                    if (pointDateYmd) {
                      const publishYmd = String(publishDate).slice(0, 10);
                      const previousYmd = visibleReviewPoints
                        .map((other) =>
                          getReviewPointDateFromPublish(publishDate, other)
                        )
                        .filter((ymd) => ymd && ymd < pointDateYmd)
                        .sort()
                        .pop();
                      const fromYmd = previousYmd
                        ? addDaysToDateString(previousYmd, 1)
                        : pointDateYmd === publishYmd
                          ? publishYmd
                          : addDaysToDateString(publishYmd, 1);
                      pointDateLabel =
                        fromYmd && fromYmd < pointDateYmd
                          ? `${formatDisplayDate(fromYmd)} – ${formatDisplayDate(
                              pointDateYmd
                            )}`
                          : formatDisplayDate(pointDateYmd);
                    }
                    return (
                      <Box
                        component="li"
                        key={point.key}
                        sx={{
                          mb: 0.75,
                          borderRadius: "10px",
                          border: `1px solid ${expanded ? PRIMARY : BORDER}`,
                          bgcolor: expanded ? "#fff" : "#f8fafc",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            py: 1.1,
                            px: 1.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 13.5,
                                color: "#0f172a",
                                fontWeight: 600,
                              }}
                            >
                              {point.label}
                              <Typography
                                component="span"
                                sx={{
                                  ml: 0.75,
                                  fontWeight: 500,
                                  color: MUTED,
                                  fontSize: 12.5,
                                }}
                              >
                                · {meta}
                              </Typography>
                            </Typography>
                            {/* Dates under the title, smaller. */}
                            {pointDateLabel ? (
                              <Typography
                                sx={{
                                  mt: 0.25,
                                  fontWeight: 500,
                                  color: PRIMARY,
                                  fontSize: 11.5,
                                }}
                              >
                                {pointDateLabel}
                              </Typography>
                            ) : null}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              flexShrink: 0,
                            }}
                          >
                            <button
                              type="button"
                              className="small-contained-outlined-button"
                              onClick={() => toggleReviewPointExtras(point.key)}
                            >
                              {expanded
                                ? "Hide"
                                : extraCount > 0 || excludedCount > 0
                                  ? "Edit Extra Questions"
                                  : "Add Extra Questions"}
                            </button>
                            {/* Points the admin added (no-end journeys) can be removed. */}
                            {durationUnlimited ? (
                              <Tooltip title={`Remove ${point.label}`}>
                                <IconButton
                                  size="small"
                                  aria-label={`Remove ${point.label}`}
                                  onClick={() => removeReviewPoint(point.key)}
                                  sx={{ color: "#dc2626" }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Box>

                        {expanded ? (
                          <Box
                            sx={{
                              px: 1.5,
                              pb: 1.5,
                              pt: 1,
                              borderTop: `1px solid ${BORDER}`,
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              bgcolor: "#fff",
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 14,
                                  color: "#0f172a",
                                  mb: 1,
                                }}
                              >
                                Shared check-in questions
                              </Typography>
                              {sharedQuestions.length === 0 ? (
                                <Typography sx={{ fontSize: 13, color: MUTED }}>
                                  No shared questions yet — add them above
                                  first.
                                </Typography>
                              ) : (
                                sharedQuestions.map((question) => {
                                  const excluded = (
                                    point.excludedSharedQuestionIds || []
                                  ).includes(question.id);
                                  return (
                                    <Box
                                      key={question.id}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        py: 0.75,
                                        borderBottom: `1px solid ${BORDER}`,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 13.5,
                                          color: excluded ? MUTED : "#0f172a",
                                          textDecoration: excluded
                                            ? "line-through"
                                            : "none",
                                        }}
                                      >
                                        {question.label || "Untitled"}
                                      </Typography>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={excluded}
                                            onChange={(e) => {
                                              const next = new Set(
                                                point.excludedSharedQuestionIds ||
                                                  []
                                              );
                                              if (e.target.checked)
                                                next.add(question.id);
                                              else next.delete(question.id);
                                              updateReviewPoint(point.key, {
                                                excludedSharedQuestionIds:
                                                  Array.from(next),
                                              });
                                            }}
                                            sx={{
                                              color: PRIMARY,
                                              "&.Mui-checked": {
                                                color: PRIMARY,
                                              },
                                            }}
                                          />
                                        }
                                        label="Exclude"
                                        sx={{
                                          m: 0,
                                          "& .MuiFormControlLabel-label": {
                                            fontSize: 12.5,
                                          },
                                        }}
                                      />
                                    </Box>
                                  );
                                })
                              )}
                            </Box>

                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: "#0f172a",
                                  }}
                                >
                                  Extra questions
                                </Typography>
                                <button
                                  type="button"
                                  className="small-contained-outlined-button"
                                  onClick={() => {
                                    const extras = [
                                      ...(point.extraQuestions || []),
                                      createBlankExtraQuestion(),
                                    ];
                                    updateReviewPoint(point.key, {
                                      extraQuestions: extras,
                                    });
                                  }}
                                >
                                  Add specific
                                </button>
                              </Box>
                              {(point.extraQuestions || []).length === 0 ? (
                                <Typography sx={{ fontSize: 13, color: MUTED }}>
                                  Nothing extra at this point yet.
                                </Typography>
                              ) : (
                                (point.extraQuestions || []).map(
                                  (question, index) => (
                                    <Box
                                      key={question.id}
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        mb: 1.25,
                                        alignItems: "center",
                                      }}
                                    >
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Extra question"
                                        // new empty field gets the cursor
                                        autoFocus={!question.label}
                                        value={question.label || ""}
                                        onChange={(e) => {
                                          const extras = [
                                            ...(point.extraQuestions || []),
                                          ];
                                          extras[index] = {
                                            ...extras[index],
                                            label: e.target.value,
                                          };
                                          updateReviewPoint(point.key, {
                                            extraQuestions: extras,
                                          });
                                        }}
                                      />
                                      <TextField
                                        select
                                        size="small"
                                        label="Type"
                                        value={
                                          question.fieldType || "short_text"
                                        }
                                        onChange={(e) => {
                                          const extras = [
                                            ...(point.extraQuestions || []),
                                          ];
                                          extras[index] = {
                                            ...extras[index],
                                            fieldType: e.target.value,
                                          };
                                          updateReviewPoint(point.key, {
                                            extraQuestions: extras,
                                          });
                                        }}
                                        sx={{ minWidth: 140 }}
                                      >
                                        {COACH_REVIEW_FIELD_TYPES.map(
                                          (option) => (
                                            <MenuItem
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </MenuItem>
                                          )
                                        )}
                                      </TextField>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          updateReviewPoint(point.key, {
                                            extraQuestions: (
                                              point.extraQuestions || []
                                            ).filter(
                                              (item) => item.id !== question.id
                                            ),
                                          });
                                        }}
                                        sx={{ color: "#dc2626" }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )
                                )
                              )}
                              {/* Save right here after adding questions (stays on this tab). */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  mt: 1.5,
                                }}
                              >
                                <button
                                  type="button"
                                  className="small-contained-button"
                                  disabled={isSaving}
                                  onClick={() => handleSave({ stay: true })}
                                >
                                  {isSaving ? "Saving…" : "Save"}
                                </button>
                              </Box>
                            </Box>
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Count + View all on one compact row; the empty text above
                  shows instead when there are no points. */}
              {reviewPointSettings.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mt: -1,
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                    {reviewPointSettings.length} schedule point
                    {reviewPointSettings.length === 1 ? "" : "s"} · shared
                    questions on every check-in
                    {sharedQuestions.length
                      ? ` (${sharedQuestions.length})`
                      : ""}
                  </Typography>
                  {scheduleNeedsViewAll ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setSchedulePointsExpanded((prev) => !prev)}
                      sx={{
                        p: 0,
                        border: 0,
                        background: "none",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                        color: PRIMARY,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {schedulePointsExpanded
                        ? "Show fewer"
                        : `View all ${reviewPointSettings.length} points`}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </SectionCard>

            {/* Add a schedule point (Ongoing journeys) — unit follows Basics. */}
            <Dialog
              open={addPointOpen}
              onClose={() => setAddPointOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle sx={{ fontSize: 17, fontWeight: 700, pb: 1 }}>
                Add schedule point
              </DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  type="number"
                  label="After"
                  value={customPointAmount}
                  onChange={(e) => setCustomPointAmount(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddSchedulePoint();
                  }}
                  inputProps={{ min: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getSchedulePointUnit(setup)}
                      </InputAdornment>
                    ),
                  }}
                  helperText="Unit follows the check-in frequency on Basics."
                  sx={{ mt: 1 }}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <button
                  type="button"
                  className="small-contained-outlined-button"
                  onClick={() => setAddPointOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="small-contained-button"
                  onClick={handleAddSchedulePoint}
                >
                  Add
                </button>
              </DialogActions>
            </Dialog>

            <SectionCard
              icon={<LocalOfferOutlinedIcon fontSize="small" />}
              title="Tags"
              description="Optional labels to organise this journey in lists and filters."
            >
              <TrackerLibraryTagPicker
                value={tags}
                onChange={(next) => updateNested("tags", next)}
                description="Select tags from the shared library for lists, filters, and automations."
              />
            </SectionCard>

            <SectionCard
              icon={<LayersOutlinedIcon fontSize="small" />}
              title="Publish settings"
            >
              <PublishSettingsDrawer
                variant="inline"
                hideHeader
                tracker={tracker}
                publicUrl={publicUrl}
                onChange={handlePublishSettingsChange}
                onPublish={handlePublish}
                publishing={isSaving}
                onEditCoaches={() => setBuilderTab("coaches")}
                onOpenQr={() => setQrPopupOpen(true)}
              />
            </SectionCard>

              <Box sx={{ height: 72 }} />
              </>
            )}

            {!isCreate && builderTab === "coaches" && showCoachWorkflow && (
              <>
            <Box sx={{ mb: 3.5, width: "100%" }}>
              <SectionCard
                icon={<GroupsOutlinedIcon fontSize="small" />}
                title="Coaches"
                subtitle="Who replies to check-ins and does the reviews on this journey."
                sx={{ mb: 0 }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}
                >
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={coachSelectOptions}
                    loading={coachesLoading}
                    value={assignedCoaches}
                    onChange={(_, value) =>
                      handleAssignedCoachesChange(value)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) =>
                      coachDisplayName(option)
                    }
                    filterSelectedOptions
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              fontSize: 10,
                              fontWeight: 700,
                              bgcolor: coachAvatarColor(option),
                            }}
                          >
                            {coachInitials(option)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 13.5,
                                fontWeight: 500,
                                color: "#0f172a",
                              }}
                            >
                              {coachDisplayName(option)}
                            </Typography>
                            {option.email ? (
                              <Typography
                                sx={{ fontSize: 11.5, color: MUTED }}
                              >
                                {option.email}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      </li>
                    )}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select coaches"
                        placeholder={
                          coachesLoading
                            ? "Loading…"
                            : assignedCoaches.length
                              ? "Add more…"
                              : "Search and select coaches"
                        }
                      />
                    )}
                  />
                  {assignedCoaches.length ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                          md: "repeat(3, minmax(0, 1fr))",
                          lg: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 0.75,
                      }}
                    >
                      {assignedCoaches.map((coach) => {
                        const selected = permissionsExpandedCoachId === coach.id;
                        return (
                          <Box
                            key={coach.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1,
                              py: 0.6,
                              minWidth: 0,
                              borderRadius: "10px",
                              border: `1px solid ${selected ? PRIMARY : BORDER}`,
                              bgcolor: selected ? PRIMARY_SOFT : "#fff",
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: 10,
                                fontWeight: 700,
                                flexShrink: 0,
                                bgcolor: coachAvatarColor(coach),
                              }}
                            >
                              {coachInitials(coach)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#0f172a",
                                  lineHeight: 1.3,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {coachDisplayName(coach)}
                              </Typography>
                              {coach.email ? (
                                <Typography
                                  sx={{
                                    fontSize: 11.5,
                                    color: MUTED,
                                    lineHeight: 1.3,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {coach.email}
                                </Typography>
                              ) : null}
                            </Box>
                            <Tooltip title="Permissions">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setPermissionsExpandedCoachId((prev) =>
                                    prev === coach.id ? null : coach.id
                                  )
                                }
                                sx={{
                                  p: 0.35,
                                  color: selected ? PRIMARY : MUTED,
                                  "&:hover": { color: PRIMARY },
                                }}
                              >
                                <ManageAccountsOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleAssignedCoachesChange(
                                    assignedCoaches.filter((c) => c.id !== coach.id)
                                  )
                                }
                                sx={{
                                  p: 0.35,
                                  color: MUTED,
                                  "&:hover": { color: "#dc2626" },
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 13, color: MUTED }}>
                      No coaches yet — pick them above.
                    </Typography>
                  )}
                  {permissionsCoach ? (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 2,
                        borderRadius: "10px",
                        border: `1px solid ${BORDER}`,
                        bgcolor: "#f8fafc",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 1.25,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13.5,
                            fontWeight: 700,
                            color: "#0f172a",
                          }}
                        >
                          Permissions ·{" "}
                          {coachDisplayName(permissionsCoach)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setPermissionsExpandedCoachId(null)
                          }
                          sx={{ color: MUTED }}
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                          },
                          gap: 1,
                        }}
                      >
                        {COACH_PERMISSION_OPTIONS.map((item) => {
                          const flags =
                            permissionsCoach.permissionMode ===
                            COACH_PERMISSION_MODES.SPECIFIC
                              ? permissionsCoach.permissions || {}
                              : createAllCoachPermissionFlags();
                          return (
                            <Box
                              key={item.key}
                              title={item.help}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1.25,
                                px: 1.25,
                                py: 0.85,
                                borderRadius: "10px",
                                border: `1px solid ${BORDER}`,
                                bgcolor: "#fff",
                                minHeight: 44,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 12.5,
                                  color: "#0f172a",
                                  lineHeight: 1.35,
                                  minWidth: 0,
                                }}
                              >
                                {item.label}
                              </Typography>
                              <Switch
                                size="small"
                                checked={Boolean(flags[item.key])}
                                onChange={(e) =>
                                  updateCoachPermissionFlag(
                                    permissionsCoach.id,
                                    item.key,
                                    e.target.checked
                                  )
                                }
                                sx={switchSx}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </SectionCard>
            </Box>

            {/* Review settings only after at least one coach is picked. */}
            {assignedCoaches.length > 0 ? (
            <>
            <Box sx={{ mb: 3.5, width: "100%" }}>
                  <SectionCard
                    icon={<RateReviewOutlinedIcon fontSize="small" />}
                    title="General coach review"
                    description=""
                    subtitle="Happens on every check-in. When a member submits a check-in, their coach replies within the time set in Reply within. A late reply shows as Overdue."
                    sx={{ mb: 0 }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: formGrid12,
                        gap: 2.5,
                        alignItems: "start",
                        width: "100%",
                      }}
                    >
                      {setup ? (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Coach replies on every check-in?"
                          sx={{ gridColumn: formCol6 }}
                          value={alsoReviewEveryCheckin ? "yes" : "no"}
                          onChange={(e) => {
                            const yes = e.target.value === "yes";
                            updateCreateSetup({
                              alsoReviewEveryCheckin: yes,
                              ...(yes
                                ? {}
                                : scheduledReviews === "none"
                                  ? { scheduledReviews: "by_week" }
                                  : {}),
                            });
                          }}
                        >
                          <MenuItem value="no">
                            No
                          </MenuItem>
                          <MenuItem value="yes">
                            Yes, the coach replies
                          </MenuItem>
                        </TextField>
                      ) : null}

                      {setup && alsoReviewEveryCheckin ? (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Reply within"
                          sx={{ gridColumn: formCol6 }}
                          value={replyWithinDays}
                          onChange={(e) =>
                            updateCreateSetup({
                              replyWithinDays: Number(e.target.value),
                              alsoReviewEveryCheckin: true,
                            })
                          }
                        >
                          {REPLY_WITHIN_DAYS_OPTIONS.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : null}

                    </Box>
                  </SectionCard>
            </Box>

            <Box sx={{ mb: 4.5, width: "100%" }}>
                <SectionCard
                  icon={<RateReviewOutlinedIcon fontSize="small" />}
                  title="Coach review"
                  description=""
                  subtitle="A bigger progress review on the weeks, days or dates you pick (e.g. Week 4, 8, 12). The coach looks at the member's progress since the start, not just one check-in. Enabled turns all coach reviews on or off, including the one on every check-in."
                  action={
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={coachReview.enabled !== false}
                          onChange={(e) =>
                            updateCoachReview({ enabled: e.target.checked })
                          }
                          sx={{
                            color: PRIMARY,
                            "&.Mui-checked": { color: PRIMARY },
                          }}
                        />
                      }
                      label="Enabled"
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: 13,
                          fontWeight: 600,
                        },
                      }}
                    />
                  }
                >
                  {coachReview.enabled === false ? (
                    <Typography
                      sx={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}
                    >
                      Coach review is off.
                    </Typography>
                  ) : null}

                  {setup && coachReview.enabled !== false ? (
                    <Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: formGrid12,
                        gap: 2.5,
                        alignItems: "start",
                        width: "100%",
                      }}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Schedule type"
                        value={scheduledReviews}
                        onChange={(e) => {
                          const value = e.target.value;
                          const patch = { scheduledReviews: value };
                          // Nothing is pre-filled — weeks, days or dates are added by hand.
                          if (value === "none") {
                            patch.alsoReviewEveryCheckin = true;
                          }
                          updateCreateSetup(patch);
                        }}
                        sx={{ gridColumn: formCol6 }}
                      >
                        {SCHEDULED_REVIEWS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      {scheduledReviews === "by_week" ? (
                        <>
                          <Box
                            sx={{
                              gridColumn: formCol6,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.5,
                              alignItems: "flex-end",
                              width: "100%",
                            }}
                          >
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Week"
                              value={reviewWeekToAdd}
                              onChange={(e) =>
                                setReviewWeekToAdd(e.target.value)
                              }
                              inputProps={{
                                min: 1,
                                max: maxReviewWeek ?? undefined,
                              }}
                              sx={{ flex: "1 1 140px", minWidth: 120 }}
                            />
                            <Box
                              sx={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "flex-end",
                              }}
                            >
                              <button
                                type="button"
                                className="small-contained-button"
                                style={{
                                  height: 44,
                                  minHeight: 44,
                                  borderRadius: 10,
                                  padding: "0 16px",
                                  boxSizing: "border-box",
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => {
                                  const week = Math.floor(
                                    Number(reviewWeekToAdd)
                                  );
                                  if (
                                    !Number.isFinite(week) ||
                                    week < 1 ||
                                    (maxReviewWeek != null &&
                                      week > maxReviewWeek)
                                  ) {
                                    enqueueSnackbar(
                                      maxReviewWeek != null
                                        ? `Week must be 1–${maxReviewWeek}.`
                                        : "Enter a valid week.",
                                      { variant: "warning" }
                                    );
                                    return;
                                  }
                                  if (selectedReviewWeeks.includes(week)) {
                                    return;
                                  }
                                  updateCreateSetup({
                                    scheduledReviews: "by_week",
                                    coachReviewWeeks: [
                                      ...selectedReviewWeeks,
                                      week,
                                    ].sort((a, b) => a - b),
                                  });
                                }}
                              >
                                Add
                              </button>
                            </Box>
                          </Box>
                          {selectedReviewWeeks.length ? (
                            <Box
                              sx={{
                                gridColumn: { xs: "1 / -1", md: "span 12" },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                }}
                              >
                                {[...selectedReviewWeeks]
                                  .sort((a, b) => a - b)
                                  .map((week) => (
                                    <Box
                                      key={week}
                                      onClick={() => {
                                        const next = selectedReviewWeeks.filter(
                                          (w) => w !== week
                                        );
                                        updateCreateSetup({
                                          scheduledReviews: "by_week",
                                          coachReviewWeeks: next,
                                        });
                                      }}
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        px: 1.25,
                                        py: 0.7,
                                        borderRadius: "8px",
                                        border: `1.5px solid ${PRIMARY}`,
                                        bgcolor: PRIMARY,
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        cursor: "pointer",
                                        userSelect: "none",
                                      }}
                                    >
                                      Week {week} ✕
                                    </Box>
                                  ))}
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                gridColumn: { xs: "1 / -1", md: "span 12" },
                                fontSize: 12.5,
                                color: MUTED,
                              }}
                            >
                              No weeks added yet.
                            </Typography>
                          )}
                        </>
                      ) : null}

                      {scheduledReviews === "by_day" ? (
                        <>
                          <Box
                            sx={{
                              gridColumn: formCol6,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.5,
                              alignItems: "flex-end",
                              width: "100%",
                            }}
                          >
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Day"
                              value={reviewDayToAdd}
                              onChange={(e) => setReviewDayToAdd(e.target.value)}
                              inputProps={{
                                min: 1,
                                max: maxReviewDay ?? undefined,
                              }}
                              sx={{ flex: "1 1 140px", minWidth: 120 }}
                            />
                            <Box
                              sx={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "flex-end",
                              }}
                            >
                              <button
                                type="button"
                                className="small-contained-button"
                                style={{
                                  height: 44,
                                  minHeight: 44,
                                  borderRadius: 10,
                                  padding: "0 16px",
                                  boxSizing: "border-box",
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => {
                                  const day = Math.floor(
                                    Number(reviewDayToAdd)
                                  );
                                  if (
                                    !Number.isFinite(day) ||
                                    day < 1 ||
                                    (maxReviewDay != null && day > maxReviewDay)
                                  ) {
                                    enqueueSnackbar(
                                      maxReviewDay != null
                                        ? `Day must be 1–${maxReviewDay}.`
                                        : "Enter a valid day.",
                                      { variant: "warning" }
                                    );
                                    return;
                                  }
                                  if (selectedReviewDays.includes(day)) {
                                    enqueueSnackbar(
                                      `Day ${day} is already added.`,
                                      { variant: "warning" }
                                    );
                                    return;
                                  }
                                  updateCreateSetup({
                                    scheduledReviews: "by_day",
                                    coachReviewDays: [
                                      ...selectedReviewDays,
                                      day,
                                    ].sort((a, b) => a - b),
                                  });
                                }}
                              >
                                Add
                              </button>
                            </Box>
                          </Box>
                          {selectedReviewDays.length ? (
                            <Box
                              sx={{
                                gridColumn: { xs: "1 / -1", md: "span 12" },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                }}
                              >
                                {[...selectedReviewDays]
                                  .sort((a, b) => a - b)
                                  .map((day) => (
                                    <Box
                                      key={day}
                                      onClick={() => {
                                        const next = selectedReviewDays.filter(
                                          (d) => d !== day
                                        );
                                        updateCreateSetup({
                                          scheduledReviews: "by_day",
                                          coachReviewDays: next,
                                        });
                                      }}
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        px: 1.25,
                                        py: 0.7,
                                        borderRadius: "8px",
                                        border: `1.5px solid ${PRIMARY}`,
                                        bgcolor: PRIMARY,
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        cursor: "pointer",
                                        userSelect: "none",
                                      }}
                                    >
                                      Day {day} ✕
                                    </Box>
                                  ))}
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                gridColumn: { xs: "1 / -1", md: "span 12" },
                                fontSize: 12.5,
                                color: MUTED,
                              }}
                            >
                              No days added yet.
                            </Typography>
                          )}
                        </>
                      ) : null}

                      {scheduledReviews === "fixed_dates" ? (
                        <>
                          <Box
                            sx={{
                              gridColumn: formCol6,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.5,
                              alignItems: "flex-end",
                              width: "100%",
                            }}
                          >
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Month"
                              value={fixedDateMonth}
                              onChange={(e) =>
                                setFixedDateMonth(Number(e.target.value))
                              }
                              sx={{ flex: "1 1 140px", minWidth: 120 }}
                            >
                              {MONTH_OPTIONS.map((month) => (
                                <MenuItem key={month.value} value={month.value}>
                                  {month.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              type="number"
                              fullWidth
                              size="small"
                              label="Day"
                              value={fixedDateDay}
                              onChange={(e) => setFixedDateDay(e.target.value)}
                              inputProps={{ min: 1, max: 28 }}
                              sx={{ flex: "0 1 96px", minWidth: 88 }}
                            />
                            <Box
                              sx={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "flex-end",
                              }}
                            >
                              <button
                                type="button"
                                className="small-contained-button"
                                style={{
                                  height: 44,
                                  minHeight: 44,
                                  borderRadius: 10,
                                  padding: "0 16px",
                                  boxSizing: "border-box",
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => {
                                  const dayNum = Math.floor(
                                    Number(fixedDateDay)
                                  );
                                  if (
                                    !Number.isFinite(dayNum) ||
                                    dayNum < 1 ||
                                    dayNum > 28
                                  ) {
                                    enqueueSnackbar(
                                      "Day must be between 1 and 28.",
                                      { variant: "warning" }
                                    );
                                    return;
                                  }
                                  const exists = fixedReviewDates.some(
                                    (d) =>
                                      d.month === fixedDateMonth &&
                                      d.day === dayNum
                                  );
                                  if (exists) return;
                                  const monthLabel =
                                    MONTH_OPTIONS.find(
                                      (m) => m.value === fixedDateMonth
                                    )?.label || "";
                                  updateCreateSetup({
                                    scheduledReviews: "fixed_dates",
                                    coachReviewFixedDates: [
                                      ...fixedReviewDates,
                                      {
                                        id: `cal_${fixedDateMonth}_${dayNum}_${Date.now()}`,
                                        label: `${monthLabel} ${dayNum}`,
                                        month: fixedDateMonth,
                                        day: dayNum,
                                      },
                                    ],
                                  });
                                }}
                              >
                                Add
                              </button>
                            </Box>
                          </Box>
                          {fixedReviewDates.length ? (
                            <Box sx={{ gridColumn: { xs: "1 / -1", md: "span 12" } }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.75,
                                }}
                              >
                                {fixedReviewDates.map((item) => {
                                  const monthLabel =
                                    MONTH_OPTIONS.find(
                                      (m) => m.value === item.month
                                    )?.label || item.month;
                                  return (
                                    <Box
                                      key={item.id}
                                      onClick={() => {
                                        const next = fixedReviewDates.filter(
                                          (d) => d.id !== item.id
                                        );
                                        updateCreateSetup({
                                          scheduledReviews: "fixed_dates",
                                          coachReviewFixedDates: next,
                                        });
                                      }}
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        px: 1.25,
                                        py: 0.7,
                                        borderRadius: "8px",
                                        border: `1.5px solid ${PRIMARY}`,
                                        bgcolor: PRIMARY,
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        cursor: "pointer",
                                        userSelect: "none",
                                      }}
                                    >
                                      {item.day} {monthLabel} ✕
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                gridColumn: { xs: "1 / -1", md: "span 12" },
                                fontSize: 12.5,
                                color: MUTED,
                              }}
                            >
                              No dates added yet.
                            </Typography>
                          )}
                        </>
                      ) : null}
                    </Box>
                    </Box>
                  ) : !setup && coachReview.enabled !== false ? (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="When coaches review"
                        value={coachReview.trigger}
                        onChange={(e) =>
                          updateCoachReview({ trigger: e.target.value })
                        }
                      >
                        {COACH_REVIEW_TRIGGERS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.title}
                          </MenuItem>
                        ))}
                      </TextField>
                  ) : null}

                  {coachReview.enabled !== false &&
                  scheduledReviews !== "none" ? (
                    <Box
                      sx={{
                        pt: 2.5,
                        mt: 0.5,
                        borderTop: `1px solid ${BORDER}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{ color: PRIMARY, flexShrink: 0, mt: 0.1 }}
                          >
                            <RateReviewOutlinedIcon fontSize="small" />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 16,
                              color: "#0f172a",
                            }}
                          >
                            Coach review questions
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            type="button"
                            className="small-contained-outlined-button"
                            onClick={() => {
                              setSelectedCoachLibraryIds([]);
                              setCoachLibrarySearch("");
                              setCoachLibraryOpen(true);
                            }}
                          >
                            From library
                          </button>
                          <button
                            type="button"
                            className="small-contained-outlined-button"
                            onClick={addCoachReviewQuestion}
                          >
                            Add specific
                          </button>
                        </Box>
                      </Box>
                  {coachReviewQuestions.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: MUTED }}>
                      No coach questions yet. Add from the library or create a
                      specific question for this tracker.
                    </Typography>
                  ) : (
                    coachReviewQuestions.map((question, index) => {
                      const fromLibrary = Boolean(question.questionId);
                      const libraryQuestion =
                        fromLibrary && question.questionId
                          ? libraryForShared.find(
                              (item) => item.id === question.questionId
                            )
                          : null;
                      const placeholderValue =
                        question.placeholder ||
                        libraryQuestion?.placeholder ||
                        "";
                      const isMeasurable =
                        libraryQuestion?.mode === QUESTION_MODES.MEASURABLE;
                      const typeDisplay = isMeasurable
                        ? getMeasureTypeLabel(libraryQuestion?.measureType)
                        : getFieldTypeLabel(
                            question.fieldType || libraryQuestion?.fieldType
                          );
                      return (
                      <Box key={question.id} sx={{ mb: 2.25 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr auto",
                              md: fromLibrary
                                ? "minmax(0, 1fr) auto auto auto"
                                : "minmax(0, 1.15fr) minmax(0, 0.95fr) minmax(140px, 0.75fr) auto auto auto",
                            },
                            gap: { xs: 1.5, md: 2 },
                            alignItems: "center",
                            pt: 0.5,
                            width: "100%",
                          }}
                        >
                          {fromLibrary ? (
                            <Box sx={{ minWidth: 0, py: 0.5 }}>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: "#0f172a",
                                  lineHeight: 1.35,
                                }}
                              >
                                {question.label || "Untitled question"}
                              </Typography>
                              {placeholderValue ? (
                                <Typography
                                  sx={{
                                    mt: 0.35,
                                    fontSize: 12.5,
                                    color: MUTED,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {placeholderValue}
                                </Typography>
                              ) : null}
                            </Box>
                          ) : (
                            <>
                              <TextField
                                fullWidth
                                size="small"
                                label="Question statement"
                                value={question.label || ""}
                                onChange={(e) =>
                                  updateCoachReviewQuestion(question.id, {
                                    label: e.target.value,
                                  })
                                }
                                sx={{
                                  gridColumn: { xs: "1 / -1", md: "auto" },
                                }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Placeholder"
                                value={placeholderValue}
                                onChange={(e) =>
                                  updateCoachReviewQuestion(question.id, {
                                    placeholder: e.target.value,
                                  })
                                }
                                sx={{
                                  gridColumn: { xs: "1 / -1", md: "auto" },
                                }}
                              />
                            </>
                          )}
                          {fromLibrary ? (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                flexShrink: 0,
                              }}
                              title={`${isMeasurable ? "Measurable" : "General"} · ${typeDisplay}`}
                            >
                              <Chip
                                size="small"
                                label={isMeasurable ? "Measurable" : "General"}
                                sx={{
                                  height: 24,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  bgcolor: PRIMARY_SOFT,
                                  color: PRIMARY,
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12.5,
                                  fontWeight: 600,
                                  color: MUTED,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {typeDisplay}
                              </Typography>
                            </Box>
                          ) : (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Type"
                              value={question.fieldType || "long_text"}
                              onChange={(e) => {
                                const fieldType = e.target.value;
                                updateCoachReviewQuestion(question.id, {
                                  fieldType,
                                  options:
                                    fieldType === "dropdown"
                                      ? question.options?.length
                                        ? question.options
                                        : ["Option 1", "Option 2"]
                                      : fieldType === "scale"
                                        ? getScaleOptions(
                                            question.scaleMin ?? 1,
                                            question.scaleMax ?? 10
                                          )
                                        : [],
                                  ...(fieldType === "scale"
                                    ? {
                                        scaleMin: question.scaleMin ?? 1,
                                        scaleMax: question.scaleMax ?? 10,
                                        placeholder:
                                          question.placeholder ||
                                          "Rate from 1 to 10",
                                      }
                                    : {}),
                                });
                              }}
                            >
                              {COACH_REVIEW_FIELD_TYPES.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={question.required !== false}
                                onChange={(e) =>
                                  updateCoachReviewQuestion(question.id, {
                                    required: e.target.checked,
                                  })
                                }
                                sx={{
                                  color: PRIMARY,
                                  "&.Mui-checked": { color: PRIMARY },
                                }}
                              />
                            }
                            label="Required"
                            sx={{
                              m: 0,
                              flexShrink: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: 13,
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              flexShrink: 0,
                            }}
                          >
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() =>
                                moveCoachReviewQuestion(index, -1)
                              }
                            >
                              <KeyboardArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={
                                index === coachReviewQuestions.length - 1
                              }
                              onClick={() => moveCoachReviewQuestion(index, 1)}
                            >
                              <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                removeCoachReviewQuestion(question.id)
                              }
                              sx={{ color: "#dc2626" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {question.fieldType === "dropdown" && !fromLibrary && (
                          // Options sit in a panel joined to their question, so it is clear whose they are.
                          <Box
                            sx={{
                              mt: 1,
                              ml: { xs: 0, md: 2 },
                              pl: 2,
                              pr: 1,
                              py: 1.5,
                              borderLeft: `3px solid ${PRIMARY}`,
                              borderRadius: "0 10px 10px 0",
                              bgcolor: "#f8fafc",
                              "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: MUTED,
                                mb: 1.25,
                              }}
                            >
                              Options for “{question.label?.trim() || "this question"}”
                            </Typography>
                            {(question.options || []).map(
                              (option, optionIndex) => (
                                <Box
                                  key={`${question.id}_opt_${optionIndex}`}
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    mb: 0.75,
                                    alignItems: "center",
                                  }}
                                >
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label={`Option ${optionIndex + 1}`}
                                    value={option}
                                    onChange={(e) => {
                                      const options = [
                                        ...(question.options || []),
                                      ];
                                      options[optionIndex] = e.target.value;
                                      updateCoachReviewQuestion(question.id, {
                                        options,
                                      });
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      updateCoachReviewQuestion(question.id, {
                                        options: (
                                          question.options || []
                                        ).filter((_, i) => i !== optionIndex),
                                      });
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              )
                            )}
                            <button
                              type="button"
                              className="small-contained-outlined-button"
                              onClick={() =>
                                updateCoachReviewQuestion(question.id, {
                                  options: [
                                    ...(question.options || []),
                                    `Option ${(question.options || []).length + 1}`,
                                  ],
                                })
                              }
                              style={{ marginTop: 4 }}
                            >
                              Add option
                            </button>
                          </Box>
                        )}
                      </Box>
                      );
                    })
                  )}

                    </Box>
                  ) : null}
                </SectionCard>
            </Box>
            </>
            ) : null}

            <Box sx={{ height: 72 }} />
              </>
            )}

            {!isCreate && builderTab === "settings" && showCoachWorkflow && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    mb: 2.5,
                    width: "100%",
                    pt: 0.75,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", mb: 0.5 }}
                    >
                      Check-in settings
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: MUTED, mb: 1.5 }}>
                      These apply only to this journey.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.1,
                          borderRadius: "10px",
                          border: `1px solid ${BORDER}`,
                          bgcolor: "#fff",
                          minHeight: 56,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 650, fontSize: 13.5 }}>
                            Members can submit a missed check-in late
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
                            {tracker.settings?.allowMissedCheckIns !== false
                              ? "On: a missed check-in can still be filled after its window closes."
                              : "Off: when the next check-in opens, the previous one closes."}
                          </Typography>
                        </Box>
                        <Switch
                          size="small"
                          checked={tracker.settings?.allowMissedCheckIns !== false}
                          onChange={(e) =>
                            updateSettings({
                              allowMissedCheckIns: e.target.checked,
                            })
                          }
                          sx={switchSx}
                          inputProps={{ "aria-label": "Allow late submit" }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.1,
                          borderRadius: "10px",
                          border: `1px solid ${BORDER}`,
                          bgcolor: "#fff",
                          minHeight: 56,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 650, fontSize: 13.5 }}>
                            Coaches can change a client&apos;s check-in day
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
                            {tracker.settings?.allowCheckInDayChange === true
                              ? "On: coaches see Change day for weekly journeys (preview first)."
                              : "Off: every client checks in on this journey's day."}
                          </Typography>
                        </Box>
                        <Switch
                          size="small"
                          checked={
                            tracker.settings?.allowCheckInDayChange === true
                          }
                          onChange={(e) =>
                            updateSettings({
                              allowCheckInDayChange: e.target.checked,
                            })
                          }
                          sx={switchSx}
                          inputProps={{ "aria-label": "Allow day change" }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      pt: 2.5,
                      borderTop: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", mb: 0.5 }}
                    >
                      Feedback media
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: MUTED, mb: 1.5 }}>
                      Limits for voice notes and videos coaches attach when
                      sending feedback on this journey.
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        type="number"
                        size="small"
                        label="Max file size (MB)"
                        value={tracker.settings?.feedbackMediaMaxMb ?? 150}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          updateSettings({
                            feedbackMediaMaxMb:
                              Number.isFinite(n) && n > 0
                                ? Math.min(500, Math.round(n))
                                : 150,
                          });
                        }}
                        inputProps={{ min: 1, max: 500 }}
                        helperText="Default 150. Hard ceiling 500 MB."
                      />
                      <TextField
                        type="number"
                        size="small"
                        label="Keep media for (days)"
                        value={
                          tracker.settings?.feedbackMediaRetentionDays ?? 0
                        }
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          updateSettings({
                            feedbackMediaRetentionDays:
                              Number.isFinite(n) && n >= 0
                                ? Math.min(3650, Math.round(n))
                                : 0,
                          });
                        }}
                        inputProps={{ min: 0, max: 3650 }}
                        helperText="0 = keep until removed. Nightly cleanup deletes older draft files."
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ height: 72 }} />
              </>
            )}
          </div>
        </div>

        <EditFormFieldsDialog
          open={Boolean(editingFormId)}
          form={editingForm}
          sharedQuestions={tracker.sharedQuestions || []}
          personalInfoQuestions={tracker.personalInfoQuestions || []}
          allForms={tracker.forms || []}
          trackerName={tracker.name}
          onClose={() => setEditingFormId(null)}
          onChange={handleFormFieldsChange}
        />

        <Dialog
          open={sharedLibraryOpen}
          onClose={() => setSharedLibraryOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: { borderRadius: "12px", overflow: "hidden" },
          }}
        >
          <DialogTitle sx={{ pr: 6, pb: 1.25 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
              Add shared questions
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
              These appear on every form by default. Exclude per form in Edit
              fields.
            </Typography>
            <IconButton
              onClick={() => setSharedLibraryOpen(false)}
              size="small"
              sx={{ position: "absolute", right: 12, top: 12 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxHeight: { xs: "70vh", sm: 480 },
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search questions"
              value={sharedLibrarySearch}
              onChange={(e) => setSharedLibrarySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[
                { value: "all", label: "All" },
                { value: "general", label: "General" },
                { value: "measurable", label: "Measurable" },
              ].map((tab) => {
                const selected = sharedLibraryTab === tab.value;
                return (
                  <Box
                    key={tab.value}
                    component="button"
                    type="button"
                    onClick={() => setSharedLibraryTab(tab.value)}
                    sx={{
                      border: `1px solid ${selected ? PRIMARY : BORDER}`,
                      bgcolor: selected ? PRIMARY_SOFT : "#fff",
                      color: selected ? PRIMARY : MUTED,
                      borderRadius: "999px",
                      px: 1.25,
                      py: 0.5,
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {tab.label}
                  </Box>
                );
              })}
            </Box>
            {filteredSharedLibrary.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>
                No more library questions to add.
              </Typography>
            ) : (
              filteredSharedLibrary.map((item) => {
                const checked = selectedSharedLibraryIds.includes(item.id);
                return (
                  <Box
                    key={item.id}
                    onClick={() =>
                      setSelectedSharedLibraryIds((prev) =>
                        checked
                          ? prev.filter((id) => id !== item.id)
                          : [...prev, item.id]
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      borderRadius: "10px",
                      border: `1px solid ${checked ? PRIMARY : BORDER}`,
                      bgcolor: checked ? PRIMARY_SOFT : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      size="small"
                      sx={{
                        color: PRIMARY,
                        "&.Mui-checked": { color: PRIMARY },
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}
                      >
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {item.mode === QUESTION_MODES.MEASURABLE
                          ? getMeasureTypeLabel(item.measureType)
                          : getFieldTypeLabel(item.fieldType)}{" "}
                        · {getQuestionModeLabel(item.mode)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label={`Edit ${item.label || "question"}`}
                      title="Edit question"
                      onClick={(e) => {
                        // Don't toggle the row's checkbox.
                        e.stopPropagation();
                        setEditingLibraryQuestion(item);
                      }}
                      sx={{ color: MUTED, "&:hover": { color: PRIMARY } }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <button
              type="button"
              className="small-contained-outlined-button"
              onClick={() => setSharedLibraryOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="small-contained-button"
              disabled={!selectedSharedLibraryIds.length}
              onClick={confirmSharedLibraryPicks}
            >
              Add {selectedSharedLibraryIds.length || ""} to every form
            </button>
          </DialogActions>
        </Dialog>

        <AddOrEditQuestionDrawer
          open={Boolean(editingLibraryQuestion)}
          question={editingLibraryQuestion}
          zIndex={(theme) => theme.zIndex.modal + 1}
          onClose={() => setEditingLibraryQuestion(null)}
          onSaved={async () => {
            // Refresh the picker with the saved changes.
            const result = await loadQuestionLibraryFromApi();
            if (result.ok) setLibraryQuestions(result.questions);
          }}
        />

        <Dialog
          open={coachLibraryOpen}
          onClose={() => setCoachLibraryOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: { borderRadius: "12px", overflow: "hidden" },
          }}
        >
          <DialogTitle sx={{ pr: 6, pb: 1.25 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
              Add coach review questions
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
              From the Questions module · Coach review type. Manage the library
              under Tracker Questions.
            </Typography>
            <IconButton
              onClick={() => setCoachLibraryOpen(false)}
              size="small"
              sx={{ position: "absolute", right: 12, top: 12 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxHeight: { xs: "70vh", sm: 480 },
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search coach review questions"
              value={coachLibrarySearch}
              onChange={(e) => setCoachLibrarySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                  </InputAdornment>
                ),
              }}
            />
            {filteredCoachLibrary.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>
                No Coach review questions left to add. Create more in Tracker
                Questions with type Coach review.
              </Typography>
            ) : (
              filteredCoachLibrary.map((item) => {
                const checked = selectedCoachLibraryIds.includes(item.id);
                return (
                  <Box
                    key={item.id}
                    onClick={() =>
                      setSelectedCoachLibraryIds((prev) =>
                        checked
                          ? prev.filter((id) => id !== item.id)
                          : [...prev, item.id]
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      borderRadius: "10px",
                      border: `1px solid ${checked ? PRIMARY : BORDER}`,
                      bgcolor: checked ? PRIMARY_SOFT : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      size="small"
                      sx={{
                        p: 0.5,
                        color: PRIMARY,
                        "&.Mui-checked": { color: PRIMARY },
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "#0f172a",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {getFieldTypeLabel(item.fieldType)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <button
              type="button"
              className="small-contained-outlined-button"
              onClick={() => setCoachLibraryOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="small-contained-button"
              disabled={!selectedCoachLibraryIds.length}
              onClick={() => {
                const picks = availableCoachLibrary.filter((item) =>
                  selectedCoachLibraryIds.includes(item.id)
                );
                addCoachReviewQuestionsFromLibrary(picks);
                setCoachLibraryOpen(false);
                setSelectedCoachLibraryIds([]);
              }}
            >
              Add {selectedCoachLibraryIds.length || ""} to coach review
            </button>
          </DialogActions>
        </Dialog>

        <TrackerQrCodePopup
          open={qrPopupOpen}
          url={publicUrl}
          trackerName={tracker.name}
          onClose={() => setQrPopupOpen(false)}
        />

        {(isCreate ||
          builderTab === "basics" ||
          builderTab === "questions" ||
          builderTab === "coaches" ||
          builderTab === "settings") && (
          <Box
            sx={{
              position: "fixed",
              right: { xs: 16, md: 24 },
              bottom: { xs: 16, md: 24 },
              zIndex: 1200,
              display: "flex",
              gap: 1,
            }}
          >
            <button
              type="button"
              className="small-contained-button"
              disabled={
                isSaving ||
                (builderTab === "basics" && !isBasicsComplete) ||
                (builderTab === "questions" && !isQuestionsComplete)
              }
              style={{
                boxShadow: "0 6px 20px rgba(15, 23, 42, 0.18)",
                ...(isSaving ||
                (builderTab === "basics" && !isBasicsComplete) ||
                (builderTab === "questions" && !isQuestionsComplete)
                  ? { opacity: 0.55, cursor: "not-allowed" }
                  : null),
              }}
              onClick={
                builderTab === "questions"
                  ? handleQuestionsSaveAndContinue
                  : handleSave
              }
            >
              {isSaving && !isPublishing
                ? "Saving…"
                : builderTab === "settings" ||
                  (builderTab === "questions" && isLeadCapture)
                ? "Save"
                : "Save & continue"}
            </button>
            {/* The only Publish button — saved drafts only. */}
            {!isCreate && !isArchived && !isLive ? (
              <button
                type="button"
                className="small-contained-button"
                disabled={isSaving}
                onClick={handlePublish}
                style={{
                  boxShadow: "0 6px 20px rgba(15, 23, 42, 0.18)",
                  ...(isSaving ? { opacity: 0.55, cursor: "not-allowed" } : null),
                }}
              >
                {isPublishing ? "Publishing…" : "Publish"}
              </button>
            ) : null}
            {/* Already live: say so where the Publish button would be. */}
            {!isCreate && !isArchived && isLive ? (
              <button
                type="button"
                className="small-contained-outlined-button"
                disabled
                title="This journey is live — members can join it."
                style={{
                  boxShadow: "0 6px 20px rgba(15, 23, 42, 0.18)",
                  background: "#fff",
                  cursor: "default",
                }}
              >
                ✓ Published
              </button>
            ) : null}
          </Box>
        )}
      </div>
    </LocalizationProvider>
  );
}
